import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiType, FiMaximize2, FiMinimize2, FiExternalLink } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import frostyImg from '../../assets/icons/Frosty.png';
import api from '../../services/api';
import CustomEmojiPicker from '../Common/EmojiPicker';
import { getCustomEmojiSizeForArea, subscribeEmojiSettings } from '../../utils/emojiSettings';
import {
  WELCOME_SCREEN_SETTING_KEYS,
  getWelcomeScreenLayerForMobileViewport,
  buildAppTitleWithWelcomeLayer,
  getMobileUnsetWelcomeFieldFallback,
  mergeAppTitleSettingsFromStorage,
  unionWelcomeGoogleFontFamiliesInSettings,
} from '../../utils/welcomeScreenSettings';
import {
  GOOGLE_FONTS_WELCOME_CATALOG,
  googleFontFamilyCssValue,
  syncWelcomeGoogleFontLinkElements,
  removeAllWelcomeGoogleFontPreviewLinks,
} from '../../utils/googleFontsWelcome';
import { welcomeScreenFillStyle, WelcomeScreenPhotoBackground } from '../WelcomeScreenHero';

// Градиенты для фона приветственного экрана
const BACKGROUND_GRADIENTS = {
  gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  gradient5: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  gradient6: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  gradient7: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  gradient8: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  gradient9: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  gradient10: 'linear-gradient(135deg, #232931 0%, #2193b0 100%)'
};

function getBackgroundGradient(gradientId) {
  return BACKGROUND_GRADIENTS[gradientId] || BACKGROUND_GRADIENTS.gradient10;
}

/** Подзаголовок в превью: parseFloat(''.replace('em','')) даёт NaN → невидимый текст. */
function getPreviewWelcomeSubtitleFontSize(settings) {
  const raw = String(settings?.welcomeScreenSubtitleFontSize ?? '1.1em').trim();
  const scale = Math.max(Number(settings?.welcomeScreenSubtitleScale) || 100, 5) / 100;
  const num = parseFloat(raw);
  if (!Number.isFinite(num) || num <= 0) return `${1.1 * scale}em`;
  if (raw.includes('px')) return `${(num / 16) * scale}em`;
  return `${num * scale}em`;
}

function getPreviewWelcomeTextScale(settings) {
  return Math.max(Number(settings?.welcomeScreenTextScale) || 100, 5) / 100;
}

/** Сброс слоя отрисовки превью при смене цветов/эффектов (иначе в модалке цвет «залипает» до полноэкранного превью). */
function getWelcomePreviewTitlePaintKey(ws) {
  if (!ws || typeof ws !== 'object') return '';
  return [
    ws.welcomeScreenColor,
    ws.welcomeScreenUseGradient,
    ws.welcomeScreenGradientStart,
    ws.welcomeScreenGradientEnd,
    ws.welcomeScreenEffectType,
    ws.welcomeScreenGlowEnabled,
    ws.welcomeScreenGlowColor,
    ws.welcomeScreenGlowIntensity,
    ws.welcomeScreenGlowSpread,
  ].join('|');
}

/** Компактный образец названия (узкая модалка + моб. превью) — видно цвет без полноэкранного режима. */
function WelcomeTitleCompactPreview({ ws }) {
  if (!ws || typeof ws !== 'object') return null;
  const effectType = ws.welcomeScreenEffectType || 'neon';
  const glowColor = String(ws.welcomeScreenGlowColor || '#43e97b').trim();
  const titleColor = ws.welcomeScreenColor || '#43e97b';
  const intensity = Number(ws.welcomeScreenGlowIntensity) || 12;
  const spread = Number(ws.welcomeScreenGlowSpread) || 32;
  const baseStyle = {
    fontFamily: ws.welcomeScreenFontFamily || 'Arial, sans-serif',
    fontSize: 'clamp(1rem, 5.2vw, 1.4rem)',
    fontWeight: 900,
    lineHeight: 1.15,
    letterSpacing: 0.55,
    display: 'inline-block',
    maxWidth: '100%',
    wordBreak: 'break-word',
  };
  const label = ws.welcomeScreenText || 'Issa Plus';

  if (ws.welcomeScreenUseGradient === true && effectType !== 'gradient-animation') {
    return (
      <span
        key={getWelcomePreviewTitlePaintKey(ws)}
        style={{
          ...baseStyle,
          background: `linear-gradient(135deg, ${ws.welcomeScreenGradientStart || '#43e97b'} 0%, ${ws.welcomeScreenGradientEnd || '#2193b0'} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {label}
      </span>
    );
  }
  if (effectType === 'gradient-animation' && ws.welcomeScreenGlowEnabled) {
    return (
      <span
        key={getWelcomePreviewTitlePaintKey(ws)}
        style={{
          ...baseStyle,
          background: `linear-gradient(-45deg, ${glowColor}, ${titleColor}, ${glowColor}, ${titleColor})`,
          backgroundSize: '400% 400%',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {label}
      </span>
    );
  }
  const style = { ...baseStyle, color: titleColor, background: 'none' };
  if (ws.welcomeScreenGlowEnabled === true) {
    switch (effectType) {
      case 'shadow':
        style.textShadow = `2px 2px 4px rgba(0,0,0,0.45), 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
        break;
      case 'outline':
        style.textShadow = `-1px -1px 0 ${glowColor}, 1px -1px 0 ${glowColor}, -1px 1px 0 ${glowColor}, 1px 1px 0 ${glowColor}`;
        break;
      case 'sparkle':
        style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}, 1px 1px 3px rgba(0,0,0,0.35)`;
        break;
      case 'new-year':
        style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
        break;
      case 'neon':
      default:
        style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread * 0.55}px ${glowColor}, 0 0 2px rgba(255,255,255,0.35)`;
        break;
    }
  } else {
    style.textShadow = 'none';
  }
  return (
    <span key={getWelcomePreviewTitlePaintKey(ws)} style={style}>
      {label}
    </span>
  );
}

const DEFAULT_SETTINGS = {
  text: 'Issa Plus',
  fontSize: '2em',
  fontFamily: 'Arial, sans-serif',
  customFontUrl: '',
  customFontDataUrl: '', // dataURL локального шрифта
  customFontName: '',
  /** Google Fonts для названия в сайдбаре (моб. редактор). */
  sidebarGoogleFontFamilies: [],
  color: '#43e97b',
  useGradient: false,
  gradientStart: '#43e97b',
  gradientEnd: '#2193b0',
  glowEnabled: true,
  glowColor: '#43e97b',
  glowIntensity: 12,
  glowSpread: 32,
  effectType: 'neon', // neon, shadow, outline, sparkle, gradient-animation
  snowEnabled: null, // null = автоматически (по сезону), true = всегда включен, false = всегда выключен
  snowmanEnabled: false, // включен ли снеговик
  snowmanImage: null, // загруженное изображение снеговика (base64 или URL)
  snowmanPositionX: 0, // позиция по X (влево-вправо) в пикселях
  snowmanPositionY: 0, // позиция по Y (вверх-вниз) в пикселях
  snowmanScale: 100, // размер в процентах (100 = 100%)
  snowmanPositionType: 'relative', // 'relative' - относительно текста, 'absolute' - абсолютно по блоку
  avatarImage: null, // загруженное изображение аватарки (base64 или URL)
  avatarPositionX: 0, // позиция аватарки по X (влево-вправо) в пикселях
  avatarPositionY: 0, // позиция аватарки по Y (вверх-вниз) в пикселях
  avatarWidth: 88, // ширина аватарки в пикселях
  avatarHeight: 88, // высота аватарки в пикселях
  // Настройки позиции и размера для текста "Issa Plus"
  textPositionX: 0, // позиция текста "Issa Plus" по X (влево-вправо) в пикселях
  textPositionY: 0, // позиция текста "Issa Plus" по Y (вверх-вниз) в пикселях
  // Настройки для текста "Мульти - мессенджер"
  subtitleText: 'Мульти - мессенджер', // текст подзаголовка
  subtitleFontSize: '1.1em', // размер шрифта подзаголовка
  subtitleColor: '#fff', // цвет подзаголовка
  subtitlePositionX: 0, // позиция подзаголовка по X (влево-вправо) в пикселях
  subtitlePositionY: 0, // позиция подзаголовка по Y (вверх-вниз) в пикселях
  // Настройки приветственного экрана (полностью отдельно от сайдбара)
  welcomeScreenText: 'Issa Plus', // текст названия для приветственного экрана (независимый от сайдбара)
  welcomeScreenSubtitleText: 'Мульти - мессенджер', // текст подзаголовка для приветственного экрана (независимый от сайдбара)
  welcomeScreenImages: [], // до 5: { image, positionX, Y, width, height, mediaType, fillWelcomeArea?, imageDisplayScale?, fitMode? … }
  // Настройки текста для приветственного экрана (независимые от сайдбара)
  welcomeScreenFontSize: '2em', // размер шрифта названия
  welcomeScreenFontFamily: 'Arial, sans-serif', // шрифт названия
  welcomeScreenCustomFontUrl: '', // URL кастомного шрифта
  welcomeScreenCustomFontDataUrl: '', // dataURL локального шрифта
  welcomeScreenCustomFontName: '', // имя кастомного шрифта
  welcomeScreenColor: '#43e97b', // цвет названия
  welcomeScreenUseGradient: false, // использовать градиент
  welcomeScreenGradientStart: '#43e97b', // начало градиента
  welcomeScreenGradientEnd: '#2193b0', // конец градиента
  welcomeScreenGlowEnabled: true, // включено ли свечение
  welcomeScreenGlowColor: '#43e97b', // цвет свечения
  welcomeScreenGlowIntensity: 12, // интенсивность свечения
  welcomeScreenGlowSpread: 32, // разброс свечения
  welcomeScreenEffectType: 'neon', // тип эффекта: neon, shadow, outline, sparkle, gradient-animation
  // Настройки подзаголовка для приветственного экрана
  welcomeScreenSubtitleFontSize: '1.1em', // размер шрифта подзаголовка
  welcomeScreenSubtitleColor: '#fff', // цвет подзаголовка
  // Позиция и масштаб для текста и подзаголовка приветственного экрана
  welcomeScreenTextPositionX: 0, // позиция текста названия по X
  welcomeScreenTextPositionY: 0, // позиция текста названия по Y
  welcomeScreenTextScale: 100, // масштаб текста названия (в процентах)
  welcomeScreenSubtitlePositionX: 0, // позиция подзаголовка по X
  welcomeScreenSubtitlePositionY: 0, // позиция подзаголовка по Y
  welcomeScreenSubtitleScale: 100, // масштаб подзаголовка (в процентах)
  // Настройки текста описания/поздравления
  welcomeScreenDescriptionText: '', // текст описания/поздравления
  welcomeScreenDescriptionFontSize: '1em', // размер шрифта описания
  welcomeScreenDescriptionFontFamily: 'Arial, sans-serif', // шрифт описания
  welcomeScreenDescriptionColor: '#fff', // цвет описания
  welcomeScreenDescriptionPositionX: 0, // позиция описания по X
  welcomeScreenDescriptionPositionY: 0, // позиция описания по Y
  welcomeScreenDescriptionScale: 100, // масштаб описания (в процентах)
  welcomeScreenDescriptionMaxWidth: 600, // максимальная ширина описания в пикселях
  // Настройки фона приветственного экрана
  welcomeScreenBackgroundType: 'gradient', // 'gradient', 'solid', 'image'
  welcomeScreenBackgroundGradient: 'gradient1', // ID градиента (gradient1-gradient10)
  welcomeScreenBackgroundColor: '#0f172a', // цвет фона (если solid)
  welcomeScreenBackgroundImage: null, // изображение фона (base64 или URL)
  // Настройка снега для приветственного экрана
  welcomeScreenSnowEnabled: false, // включен ли снег (независимо от сайдбара)
  /** Семейства Google Fonts, добавленные в моб. редакторе (подключаются через fonts.googleapis.com). */
  welcomeScreenGoogleFontFamilies: [],
  /** Переопределения приветствия для мобильной модалки (пустой объект = как десктоп). */
  welcomeScreenMobile: {},
};

function coerceNumericSettingKey(key, value) {
  if (
    key === 'glowIntensity' ||
    key === 'glowSpread' ||
    key === 'avatarPositionX' ||
    key === 'avatarPositionY' ||
    key === 'avatarWidth' ||
    key === 'avatarHeight' ||
    key === 'snowmanPositionX' ||
    key === 'snowmanPositionY' ||
    key === 'snowmanScale' ||
    key === 'textPositionX' ||
    key === 'textPositionY' ||
    key === 'subtitlePositionX' ||
    key === 'subtitlePositionY' ||
    key === 'welcomeScreenTextPositionX' ||
    key === 'welcomeScreenTextPositionY' ||
    key === 'welcomeScreenTextScale' ||
    key === 'welcomeScreenSubtitlePositionX' ||
    key === 'welcomeScreenSubtitlePositionY' ||
    key === 'welcomeScreenSubtitleScale' ||
    key === 'welcomeScreenDescriptionPositionX' ||
    key === 'welcomeScreenDescriptionPositionY' ||
    key === 'welcomeScreenDescriptionScale' ||
    key === 'welcomeScreenDescriptionMaxWidth' ||
    key === 'welcomeScreenGlowIntensity' ||
    key === 'welcomeScreenGlowSpread'
  ) {
    const numValue = parseInt(value, 10);
    return Number.isNaN(numValue) ? 0 : numValue;
  }
  return value;
}

/** #rgb / #rrggbb для input[type=color] (короткие hex иначе ломают выбор). */
function normalizeHexForColorInput(cssColor) {
  if (cssColor == null || cssColor === '') return '#ffffff';
  let s = String(cssColor).trim();
  if (!s.startsWith('#')) {
    if (/^[0-9a-fA-F]{6}$/.test(s)) s = `#${s}`;
    else return '#ffffff';
  }
  if (s.length === 4 && s[0] === '#') {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  if (s.length === 7) return s.toLowerCase();
  return '#ffffff';
}

function getAppTitleSettings() {
  try {
    const saved = localStorage.getItem('appTitleSettings');
    if (saved) {
      return mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, JSON.parse(saved));
    }
  } catch (e) {
    console.error('Error loading app title settings:', e);
  }
  return DEFAULT_SETTINGS;
}

/** Исходник без canvas — без потерь; лимиты под типичный localStorage. */
const WELCOME_BG_ORIGINAL_MAX_BYTES = 4 * 1024 * 1024;
const WELCOME_BG_ORIGINAL_MAX_EDGE = 4096;
const WELCOME_BG_FALLBACK_MAX_W = 3840;
const WELCOME_BG_FALLBACK_MAX_H = 2160;
const WELCOME_BG_ENCODE_QUALITY = 1;

// Функция для сжатия изображения (вписывание в maxWidth×maxHeight без увеличения мелких картинок)
function compressImage(base64String, maxWidth = 300, maxHeight = 300, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width < 1 || height < 1) {
        reject(new Error('Invalid image dimensions'));
        return;
      }

      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Проверяем, есть ли прозрачность в исходном изображении
      let hasTransparency = false;
      
      // Сначала проверяем по MIME типу в base64 строке
      if (base64String.includes('image/png') || base64String.includes('data:image/png')) {
        hasTransparency = true;
      } else {
        // Если не PNG, проверяем наличие прозрачных пикселей
        try {
          // Создаем временный canvas для проверки прозрачности
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = img.width;
          tempCanvas.height = img.height;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(img, 0, 0);
          
          // Проверяем только небольшую область для производительности
          const checkWidth = Math.min(img.width, 50);
          const checkHeight = Math.min(img.height, 50);
          const imageData = tempCtx.getImageData(0, 0, checkWidth, checkHeight);
          const data = imageData.data;
          
          for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) { // alpha канал меньше 255
              hasTransparency = true;
              break;
            }
          }
        } catch (e) {
          // Если не удалось проверить (CORS и т.д.), предполагаем что нет прозрачности
          hasTransparency = false;
        }
      }
      
      // Если нет прозрачности, заливаем белым фоном (для JPEG)
      if (!hasTransparency) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
      }
      
      // Рисуем изображение
      ctx.drawImage(img, 0, 0, width, height);

      // PNG при альфе; иначе WebP (чётче и компактнее при том же качестве), fallback — JPEG
      let compressedBase64;
      if (hasTransparency) {
        compressedBase64 = canvas.toDataURL('image/png');
      } else {
        const q = Math.min(1, Math.max(0.01, quality));
        const webp = canvas.toDataURL('image/webp', q);
        compressedBase64 = webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/jpeg', q);
      }
      resolve(compressedBase64);
    };
    img.onerror = reject;
    img.src = base64String;
  });
}

function setAppTitleSettings(settings) {
  try {
    const jsonString = JSON.stringify(settings);
    const sizeInMB = new Blob([jsonString]).size / (1024 * 1024);
    
    // Проверяем размер перед сохранением (современные браузеры обычно дают больше места под ключ)
    if (sizeInMB > 10) {
      console.warn('Размер данных превышает 10MB:', sizeInMB.toFixed(2), 'MB');
      const settingsWithoutImages = {
        ...settings,
        avatarImage: null,
        snowmanImage: null,
        welcomeScreenBackgroundImage: null,
      };
      localStorage.setItem('appTitleSettings', JSON.stringify(settingsWithoutImages));
      window.dispatchEvent(new CustomEvent('appTitleSettingsUpdated', { detail: settingsWithoutImages }));
      alert(
        'Размер настроек слишком большой для localStorage. Удалены аватар, снеговик и фон приветствия. Сохраните тяжёлые картинки в более сжатом виде или очистите хранилище.'
      );
    } else {
      localStorage.setItem('appTitleSettings', jsonString);
      window.dispatchEvent(new CustomEvent('appTitleSettingsUpdated', { detail: settings }));
    }
  } catch (error) {
    console.error('Ошибка при сохранении настроек:', error);
    // Пробуем сохранить без изображений
    const settingsWithoutImages = {
      ...settings,
      avatarImage: null,
      snowmanImage: null,
      welcomeScreenBackgroundImage: null,
    };
    try {
      localStorage.setItem('appTitleSettings', JSON.stringify(settingsWithoutImages));
      window.dispatchEvent(new CustomEvent('appTitleSettingsUpdated', { detail: settingsWithoutImages }));
      alert('Изображения были удалены из-за ошибки сохранения (часто переполнение localStorage). Попробуйте файлы меньшего размера.');
    } catch (e) {
      console.error('Критическая ошибка: невозможно сохранить настройки', e);
      alert('Ошибка сохранения настроек. Возможно, localStorage переполнен. Попробуйте очистить данные браузера.');
    }
  }
}

// Функции для работы с пресетами
function getPresets() {
  try {
    const saved = localStorage.getItem('appTitlePresets');
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    console.error('Error loading presets:', e);
    return [];
  }
}

function savePreset(preset) {
  try {
    const presets = getPresets();
    const existingIndex = presets.findIndex(p => p.id === preset.id);
    
    if (existingIndex >= 0) {
      presets[existingIndex] = preset;
    } else {
      presets.push(preset);
    }
    
    localStorage.setItem('appTitlePresets', JSON.stringify(presets));
    return true;
  } catch (e) {
    console.error('Error saving preset:', e);
    return false;
  }
}

function deletePreset(presetId) {
  try {
    const presets = getPresets();
    const filtered = presets.filter(p => p.id !== presetId);
    localStorage.setItem('appTitlePresets', JSON.stringify(filtered));
    return true;
  } catch (e) {
    console.error('Error deleting preset:', e);
    return false;
  }
}

/** Узкий экран: модалка на всю ширину, удобный ввод, перетаскивание пальцем. */
const COMPACT_MODAL_BREAKPOINT_PX = 768;

function getClientXY(e) {
  if (e && typeof e.clientX === 'number') {
    return { x: e.clientX, y: e.clientY };
  }
  return { x: 0, y: 0 };
}

function resolveWelcomePinchTarget(clientX, clientY, containerEl) {
  if (!containerEl || typeof document === 'undefined') return null;
  const el = document.elementFromPoint(clientX, clientY);
  if (!el || !containerEl.contains(el)) return null;
  const marked = el.closest('[data-welcome-pinch-target]');
  if (!marked || !containerEl.contains(marked)) return null;
  return marked.getAttribute('data-welcome-pinch-target') || null;
}

export default function AppTitleSettingsModal({ open, onClose }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const settingsRef = useRef(settings);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showPresetInput, setShowPresetInput] = useState(false);
  const [activeTab, setActiveTab] = useState('sidebar'); // 'sidebar' или 'welcome'
  // Состояния для drag-and-drop изображений в превью
  const [draggingImageIndex, setDraggingImageIndex] = useState(null);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [selectedImageIndex, setSelectedImageIndex] = useState(null); // Выбранное изображение для настройки
  // Состояния для drag-and-drop текста, подзаголовка и описания
  const [draggingTextElement, setDraggingTextElement] = useState(null); // 'text', 'subtitle', 'description'
  const [dragTextStartPos, setDragTextStartPos] = useState({ x: 0, y: 0 });
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const dragTextStartPosRef = useRef({ x: 0, y: 0 });
  const dragLastPosRef = useRef(null);
  const activePreviewContainerRef = useRef(null);
  const fullscreenDragRef = useRef(false);
  /** Состояние pinch: { targetKey, startDist, baseScale } или для картинки — index, isPhoto, basePhotoScale, baseW, baseH */
  const pinchStateRef = useRef(null);
  const previewWelcomeSettingsRef = useRef(null);
  const [compactModalLayout, setCompactModalLayout] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < COMPACT_MODAL_BREAKPOINT_PX
  );
  // Состояния для rich text editor описания
  const [showEmoji, setShowEmoji] = useState(false);
  const descriptionEditorRef = useRef(null);
  /** Выделение в contentEditable до тапа по тулбару/палитре (на мобильных иначе сбрасывается). */
  const descriptionSavedRangeRef = useRef(null);
  const [textColor, setTextColor] = useState('#2193b0');
  const [bgColor, setBgColor] = useState('#fff59d');
  const [customEmojiMap, setCustomEmojiMap] = useState({});
  const [emojiSizes, setEmojiSizes] = useState({
    welcome: getCustomEmojiSizeForArea('welcome') || 24
  });
  // Состояние для полноэкранного превью
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [googleFontsPickerOpen, setGoogleFontsPickerOpen] = useState(false);
  /** Куда применить шрифт в листе Google: приветствие (название/описание) или сайдбар. */
  const googleFontsPickerApplyToRef = useRef('title');
  const [googleFontsSearch, setGoogleFontsSearch] = useState('');
  const [manualGoogleFontName, setManualGoogleFontName] = useState('');
  /** Редактор приветствия: десктоп (центральная область) или мобильная модалка. */
  const [welcomeScreenEditorVariant, setWelcomeScreenEditorVariant] = useState('desktop');
  const welcomeScreenEditorVariantRef = useRef('desktop');
  useEffect(() => {
    welcomeScreenEditorVariantRef.current = welcomeScreenEditorVariant;
  }, [welcomeScreenEditorVariant]);

  useEffect(() => {
    const onResize = () => {
      setCompactModalLayout(window.innerWidth < COMPACT_MODAL_BREAKPOINT_PX);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const previewWelcomeSettings = useMemo(() => {
    if (activeTab !== 'welcome') return settings;
    if (welcomeScreenEditorVariant === 'desktop') return settings;
    const mobile =
      settings.welcomeScreenMobile && typeof settings.welcomeScreenMobile === 'object'
        ? settings.welcomeScreenMobile
        : {};
    const layer = getWelcomeScreenLayerForMobileViewport(settings, mobile);
    return buildAppTitleWithWelcomeLayer(settings, layer);
  }, [settings, activeTab, welcomeScreenEditorVariant]);

  useEffect(() => {
    previewWelcomeSettingsRef.current = previewWelcomeSettings;
  }, [previewWelcomeSettings]);

  const patchWelcomeKey = useCallback((key, value) => {
    const processedValue = coerceNumericSettingKey(key, value);
    if (welcomeScreenEditorVariantRef.current === 'desktop') {
      setSettings((prev) => ({ ...prev, [key]: processedValue }));
    } else {
      setSettings((prev) => ({
        ...prev,
        welcomeScreenMobile: { ...(prev.welcomeScreenMobile || {}), [key]: processedValue },
      }));
    }
    setHasChanges(true);
  }, []);

  const addWelcomeGoogleFont = useCallback((rawName) => {
    const t = String(rawName || '').trim();
    if (!t) return;
    const pickerTarget = googleFontsPickerApplyToRef.current;
    if (pickerTarget === 'sidebar') {
      setSettings((prev) => {
        const cur = Array.isArray(prev.sidebarGoogleFontFamilies) ? [...prev.sidebarGoogleFontFamilies] : [];
        if (!cur.includes(t)) cur.push(t);
        const nextFont = googleFontFamilyCssValue(t);
        const next = { ...prev, sidebarGoogleFontFamilies: cur, fontFamily: nextFont };
        queueMicrotask(() => {
          try {
            setAppTitleSettings(mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, next));
          } catch (err) {
            console.warn('Не удалось сохранить список шрифтов сайдбара локально:', err);
          }
        });
        return next;
      });
      setHasChanges(true);
      setGoogleFontsPickerOpen(false);
      setManualGoogleFontName('');
      setGoogleFontsSearch('');
      return;
    }
    const applyTo = pickerTarget === 'description' ? 'description' : 'title';
    setSettings((prev) => {
      const isMobile = welcomeScreenEditorVariantRef.current !== 'desktop';
      const mobSlice = { ...(prev.welcomeScreenMobile || {}) };
      const cur = Array.isArray(
        isMobile ? mobSlice.welcomeScreenGoogleFontFamilies : prev.welcomeScreenGoogleFontFamilies
      )
        ? [...(isMobile ? mobSlice.welcomeScreenGoogleFontFamilies : prev.welcomeScreenGoogleFontFamilies)]
        : [];
      if (!cur.includes(t)) cur.push(t);
      const nextFont = googleFontFamilyCssValue(t);
      let next;
      if (isMobile) {
        const mobNext = {
          ...mobSlice,
          welcomeScreenGoogleFontFamilies: cur,
        };
        if (applyTo === 'description') {
          mobNext.welcomeScreenDescriptionFontFamily = nextFont;
        } else {
          mobNext.welcomeScreenFontFamily = nextFont;
        }
        next = {
          ...prev,
          welcomeScreenMobile: mobNext,
        };
      } else {
        const rootNext = {
          ...prev,
          welcomeScreenGoogleFontFamilies: cur,
        };
        if (applyTo === 'description') {
          rootNext.welcomeScreenDescriptionFontFamily = nextFont;
        } else {
          rootNext.welcomeScreenFontFamily = nextFont;
        }
        next = rootNext;
      }
      queueMicrotask(() => {
        try {
          setAppTitleSettings(mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, next));
        } catch (err) {
          console.warn('Не удалось сохранить список шрифтов локально:', err);
        }
      });
      return next;
    });
    setHasChanges(true);
    setGoogleFontsPickerOpen(false);
    setManualGoogleFontName('');
    setGoogleFontsSearch('');
  }, []);

  const removeWelcomeGoogleFont = useCallback((rawName) => {
    const t = String(rawName || '').trim();
    if (!t) return;
    const removedCss = googleFontFamilyCssValue(t);
    setSettings((prev) => {
      const isMobile = welcomeScreenEditorVariantRef.current !== 'desktop';
      const mobSlice = { ...(prev.welcomeScreenMobile || {}) };
      const baseList = isMobile ? mobSlice.welcomeScreenGoogleFontFamilies : prev.welcomeScreenGoogleFontFamilies;
      const cur = (Array.isArray(baseList) ? baseList : []).filter((x) => x !== t);
      const currentTitleFam = isMobile ? mobSlice.welcomeScreenFontFamily : prev.welcomeScreenFontFamily;
      const currentDescFam = isMobile ? mobSlice.welcomeScreenDescriptionFontFamily : prev.welcomeScreenDescriptionFontFamily;
      const resetTitle = currentTitleFam === removedCss ? 'Arial, sans-serif' : currentTitleFam;
      const resetDesc = currentDescFam === removedCss ? 'Arial, sans-serif' : currentDescFam;
      const patchTitle = resetTitle !== currentTitleFam ? { welcomeScreenFontFamily: resetTitle } : {};
      const patchDesc = resetDesc !== currentDescFam ? { welcomeScreenDescriptionFontFamily: resetDesc } : {};
      let next;
      if (isMobile) {
        next = {
          ...prev,
          welcomeScreenMobile: {
            ...mobSlice,
            welcomeScreenGoogleFontFamilies: cur,
            ...patchTitle,
            ...patchDesc,
          },
        };
      } else {
        next = {
          ...prev,
          welcomeScreenGoogleFontFamilies: cur,
          ...patchTitle,
          ...patchDesc,
        };
      }
      queueMicrotask(() => {
        try {
          setAppTitleSettings(mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, next));
        } catch (err) {
          console.warn('Не удалось сохранить список шрифтов локально:', err);
        }
      });
      return next;
    });
    setHasChanges(true);
  }, []);

  const removeSidebarGoogleFont = useCallback((rawName) => {
    const t = String(rawName || '').trim();
    if (!t) return;
    const removedCss = googleFontFamilyCssValue(t);
    setSettings((prev) => {
      const cur = (Array.isArray(prev.sidebarGoogleFontFamilies) ? prev.sidebarGoogleFontFamilies : []).filter(
        (x) => x !== t
      );
      const resetFam = prev.fontFamily === removedCss ? 'Arial, sans-serif' : prev.fontFamily;
      const next = {
        ...prev,
        sidebarGoogleFontFamilies: cur,
        ...(resetFam !== prev.fontFamily ? { fontFamily: resetFam } : {}),
      };
      queueMicrotask(() => {
        try {
          setAppTitleSettings(mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, next));
        } catch (err) {
          console.warn('Не удалось сохранить список шрифтов сайдбара локально:', err);
        }
      });
      return next;
    });
    setHasChanges(true);
  }, []);

  const getEffectiveWelcomeField = (prev, key) => {
    if (welcomeScreenEditorVariantRef.current !== 'mobile') return prev[key];
    const mob = prev.welcomeScreenMobile;
    if (mob && Object.prototype.hasOwnProperty.call(mob, key)) return mob[key];
    return getMobileUnsetWelcomeFieldFallback(prev, key);
  };

  const copyWelcomeDesktopToMobile = useCallback(() => {
    const slice = {};
    for (const k of WELCOME_SCREEN_SETTING_KEYS) {
      slice[k] = settings[k];
    }
    setSettings((prev) => ({ ...prev, welcomeScreenMobile: slice }));
    setHasChanges(true);
  }, [settings]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadFromServer = async () => {
      try {
        const response = await api.get('/api/app-title-settings');
        if (cancelled) return;
        if (response?.data?.success && response.data.settings) {
          let localParsed = null;
          try {
            const ls = localStorage.getItem('appTitleSettings');
            if (ls) localParsed = JSON.parse(ls);
          } catch (e) {
            /* ignore */
          }
          const merged = unionWelcomeGoogleFontFamiliesInSettings(
            DEFAULT_SETTINGS,
            response.data.settings,
            localParsed
          );
          setSettings(merged);
          setHasChanges(false);
          try {
            const normalized = mergeAppTitleSettingsFromStorage(DEFAULT_SETTINGS, merged);
            localStorage.setItem('appTitleSettings', JSON.stringify(normalized));
            window.dispatchEvent(new CustomEvent('appTitleSettingsUpdated', { detail: normalized }));
          } catch (e) {
            console.warn('⚠️ Не удалось сохранить appTitleSettings локально:', e);
          }
        }
      } catch (e) {
        console.warn('⚠️ Не удалось загрузить appTitleSettings с сервера:', e);
      }
    };
    loadFromServer();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Обработчик загрузки файла шрифта - сразу применяет шрифт
  const handleFontFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      // Сбрасываем значение input, чтобы можно было выбрать тот же файл снова
      e.target.value = '';
      return;
    }
    
    // Проверяем тип файла
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert('Неверный формат файла. Пожалуйста, выберите файл .ttf, .otf, .woff или .woff2');
      e.target.value = '';
      return;
    }
    
    const reader = new FileReader();
    reader.onerror = (error) => {
      console.error('Ошибка при чтении файла шрифта:', error);
      alert('Ошибка при чтении файла шрифта. Пожалуйста, попробуйте другой файл.');
      e.target.value = '';
    };
    reader.onload = (event) => {
      try {
        const dataUrl = event.target.result;
        // Извлекаем имя шрифта из имени файла (убираем расширение)
        let fontName = file.name.replace(/\.[^.]+$/, '');
        // Очищаем имя от специальных символов, которые могут вызвать проблемы
        fontName = fontName.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim();
        
        if (!fontName) {
          fontName = 'CustomFont';
        }
        
        const fontFamilyValue = `'${fontName}', Arial, sans-serif`;
        
        // Обновляем все поля одновременно
        setSettings(prev => ({
          ...prev,
          customFontUrl: '',
          customFontDataUrl: dataUrl,
          customFontName: fontName,
          fontFamily: fontFamilyValue
        }));
        setHasChanges(true);
        
        // Сбрасываем input, чтобы можно было выбрать файл снова
        e.target.value = '';
        
        console.log('Шрифт успешно загружен и применён:', fontName, fontFamilyValue);
      } catch (error) {
        console.error('Ошибка при обработке файла шрифта:', error);
        alert('Ошибка при обработке файла шрифта. Пожалуйста, попробуйте другой файл.');
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  // Обработчик загрузки файла шрифта для приветственного экрана
  const handleWelcomeScreenFontFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }
    
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert('Неверный формат файла. Пожалуйста, выберите файл .ttf, .otf, .woff или .woff2');
      e.target.value = '';
      return;
    }
    
    const reader = new FileReader();
    reader.onerror = (error) => {
      console.error('Ошибка при чтении файла шрифта:', error);
      alert('Ошибка при чтении файла шрифта. Пожалуйста, попробуйте другой файл.');
      e.target.value = '';
    };
    reader.onload = (event) => {
      try {
        const dataUrl = event.target.result;
        let fontName = file.name.replace(/\.[^.]+$/, '');
        fontName = fontName.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim();
        
        if (!fontName) {
          fontName = 'CustomFont';
        }
        
        const fontFamilyValue = `'${fontName}', Arial, sans-serif`;
        
        setSettings((prev) => {
          if (welcomeScreenEditorVariantRef.current === 'mobile') {
            return {
              ...prev,
              welcomeScreenMobile: {
                ...(prev.welcomeScreenMobile || {}),
                welcomeScreenCustomFontUrl: '',
                welcomeScreenCustomFontDataUrl: dataUrl,
                welcomeScreenCustomFontName: fontName,
                welcomeScreenFontFamily: fontFamilyValue,
              },
            };
          }
          return {
            ...prev,
            welcomeScreenCustomFontUrl: '',
            welcomeScreenCustomFontDataUrl: dataUrl,
            welcomeScreenCustomFontName: fontName,
            welcomeScreenFontFamily: fontFamilyValue,
          };
        });
        setHasChanges(true);
        
        e.target.value = '';
        
        console.log('Шрифт для приветственного экрана успешно загружен и применён:', fontName, fontFamilyValue);
      } catch (error) {
        console.error('Ошибка при обработке файла шрифта:', error);
        alert('Ошибка при обработке файла шрифта. Пожалуйста, попробуйте другой файл.');
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (open) {
      const saved = getAppTitleSettings();
      console.log('📥 [AppTitleSettingsModal] Загружаем настройки при открытии модалки');
      console.log('📥 [AppTitleSettingsModal] customFontName:', saved.customFontName);
      console.log('📥 [AppTitleSettingsModal] customFontDataUrl:', saved.customFontDataUrl ? 'Present (' + (saved.customFontDataUrl.length / 1024).toFixed(2) + ' KB)' : 'Missing');
      console.log('📥 [AppTitleSettingsModal] fontFamily:', saved.fontFamily);
      setSettings(saved);
      setHasChanges(false);
      setPresets(getPresets());
      setPresetName('');
      setShowPresetInput(false);
    }
  }, [open]);

  // Загрузка кастомного шрифта для приветственного экрана в превью
  useEffect(() => {
    // Загрузка кастомного шрифта по URL для приветственного экрана
    if (previewWelcomeSettings.welcomeScreenCustomFontUrl) {
      const existingLink = document.querySelector(`link[data-welcome-custom-font-preview="${previewWelcomeSettings.welcomeScreenCustomFontUrl}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = previewWelcomeSettings.welcomeScreenCustomFontUrl;
        link.setAttribute('data-welcome-custom-font-preview', previewWelcomeSettings.welcomeScreenCustomFontUrl);
        document.head.appendChild(link);
      }
    }
    
    // Загрузка кастомного шрифта из локального файла для приветственного экрана
    if (previewWelcomeSettings.welcomeScreenCustomFontDataUrl && previewWelcomeSettings.welcomeScreenCustomFontName) {
      const prevStyle = document.getElementById('welcome-custom-font-style-preview');
      if (prevStyle) prevStyle.remove();
      const style = document.createElement('style');
      style.id = 'welcome-custom-font-style-preview';
      style.innerHTML = `@font-face { font-family: '${previewWelcomeSettings.welcomeScreenCustomFontName}'; src: url('${previewWelcomeSettings.welcomeScreenCustomFontDataUrl}'); font-display: swap; }`;
      document.head.appendChild(style);
    } else {
      const prevStyle = document.getElementById('welcome-custom-font-style-preview');
      if (prevStyle) prevStyle.remove();
    }
  }, [previewWelcomeSettings.welcomeScreenCustomFontUrl, previewWelcomeSettings.welcomeScreenCustomFontDataUrl, previewWelcomeSettings.welcomeScreenCustomFontName]);

  useEffect(() => {
    const w = Array.isArray(settings.welcomeScreenGoogleFontFamilies) ? settings.welcomeScreenGoogleFontFamilies : [];
    const m = Array.isArray(settings.welcomeScreenMobile?.welcomeScreenGoogleFontFamilies)
      ? settings.welcomeScreenMobile.welcomeScreenGoogleFontFamilies
      : [];
    const sb = Array.isArray(settings.sidebarGoogleFontFamilies) ? settings.sidebarGoogleFontFamilies : [];
    const merged = [...new Set([...w, ...m, ...sb].map((f) => String(f).trim()).filter(Boolean))];
    syncWelcomeGoogleFontLinkElements(merged, { preview: true });
    return () => {
      removeAllWelcomeGoogleFontPreviewLinks();
    };
  }, [
    settings.welcomeScreenGoogleFontFamilies,
    settings.welcomeScreenMobile?.welcomeScreenGoogleFontFamilies,
    settings.sidebarGoogleFontFamilies,
  ]);

  // Load custom emojis list for inline rendering
  useEffect(() => {
    fetch('/api/emojis/list')
      .then(r => r.json())
      .then(list => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach(e => { map[`custom:${e.name}`] = e.url; });
        setCustomEmojiMap(map);
      })
      .catch(() => {});
  }, []);
  
  // Подписка на обновления настроек размеров эмодзи
  useEffect(() => {
    const unsubscribe = subscribeEmojiSettings(() => {
      setEmojiSizes({ welcome: getCustomEmojiSizeForArea('welcome') || 24 });
    });
    return unsubscribe;
  }, []);
  
  // Keep editor DOM in sync only when content is changed programmatically
  useEffect(() => {
    if (descriptionEditorRef.current && typeof previewWelcomeSettings.welcomeScreenDescriptionText === 'string') {
      // Avoid unnecessary writes: only set when different
      if (descriptionEditorRef.current.innerHTML !== previewWelcomeSettings.welcomeScreenDescriptionText) {
        descriptionEditorRef.current.innerHTML = previewWelcomeSettings.welcomeScreenDescriptionText;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewWelcomeSettings.welcomeScreenDescriptionText]);

  // Обработка клавиши ESC для закрытия полноэкранного превью
  useEffect(() => {
    if (!showFullscreenPreview) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowFullscreenPreview(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showFullscreenPreview]);

  const handleChange = (key, value) => {
    const processedValue = coerceNumericSettingKey(key, value);
    const newSettings = { ...settings, [key]: processedValue };
    setSettings(newSettings);
    setHasChanges(true);
  };

  // Перетаскивание изображений: Pointer Events (мышь + палец)
  const handleImagePointerDown = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const previewContainer = e.currentTarget.closest('[data-preview-container]');
    if (!previewContainer) return;
    activePreviewContainerRef.current = previewContainer;
    fullscreenDragRef.current = previewContainer.dataset?.fullscreenPreview === 'true';
    if (typeof e.pointerId === 'number' && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
    }
    const { x, y } = getClientXY(e);
    setDraggingImageIndex(index);
    dragLastPosRef.current = { x, y };
    const previewRect = previewContainer.getBoundingClientRect();
    const img = previewWelcomeSettings.welcomeScreenImages[index];
    const centerX = previewRect.left + previewRect.width / 2;
    const centerY = previewRect.top + previewRect.height / 2;
    const currentImgCenterX = centerX + (img?.positionX || 0);
    const currentImgCenterY = centerY + (img?.positionY || 0);
    const start = {
      x: x - currentImgCenterX,
      y: y - currentImgCenterY,
    };
    dragStartPosRef.current = start;
    setDragStartPos(start);
  };

  const handlePreviewPointerMoveOnSurface = (e) => {
    e.stopPropagation();
    if (e.currentTarget?.dataset?.fullscreenPreview === 'true') {
      try {
        e.preventDefault();
      } catch (err) {
        /* ignore */
      }
    }
  };

  const endPreviewDrag = useCallback((e) => {
    if (e) {
      e.stopPropagation();
    }
    activePreviewContainerRef.current = null;
    fullscreenDragRef.current = false;
    setDraggingImageIndex(null);
    dragStartPosRef.current = { x: 0, y: 0 };
    setDragStartPos({ x: 0, y: 0 });
    setDraggingTextElement(null);
    dragTextStartPosRef.current = { x: 0, y: 0 };
    setDragTextStartPos({ x: 0, y: 0 });
    dragLastPosRef.current = null;
  }, []);

  useEffect(() => {
    if (draggingImageIndex === null && !draggingTextElement) return;

    const processMove = (clientX, clientY, movementX, movementY) => {
      const last = dragLastPosRef.current || { x: clientX, y: clientY };
      const deltaX = movementX || (clientX - last.x);
      const deltaY = movementY || (clientY - last.y);
      dragLastPosRef.current = { x: clientX, y: clientY };

      const isFs = fullscreenDragRef.current;
      const container = activePreviewContainerRef.current;

      if (draggingImageIndex !== null) {
        setSettings((prev) => {
          const placeImage = (images, idx, nx, ny) => {
            const next = [...images];
            const im = next[idx];
            if (!im) return null;
            next[idx] = { ...im, positionX: Math.round(nx), positionY: Math.round(ny) };
            return next;
          };

          if (welcomeScreenEditorVariantRef.current === 'mobile') {
            const mob = { ...(prev.welcomeScreenMobile || {}) };
            const baseImgs = prev.welcomeScreenImages || [];
            const effective = mob.welcomeScreenImages !== undefined ? mob.welcomeScreenImages : baseImgs;
            const images = [...effective];
            const img = images[draggingImageIndex];
            if (!img) return prev;
            let nx;
            let ny;
            if (isFs) {
              nx = (img.positionX || 0) + deltaX;
              ny = (img.positionY || 0) + deltaY;
            } else if (container) {
              const rect = container.getBoundingClientRect();
              const cx = rect.width / 2;
              const cy = rect.height / 2;
              const ds = dragStartPosRef.current;
              nx = clientX - rect.left - cx - ds.x;
              ny = clientY - rect.top - cy - ds.y;
            } else {
              nx = (img.positionX || 0) + deltaX;
              ny = (img.positionY || 0) + deltaY;
            }
            const updated = placeImage(images, draggingImageIndex, nx, ny);
            return updated ? { ...prev, welcomeScreenMobile: { ...mob, welcomeScreenImages: updated } } : prev;
          }

          const images = [...(prev.welcomeScreenImages || [])];
          const img = images[draggingImageIndex];
          if (!img) return prev;
          let nx;
          let ny;
          if (isFs) {
            nx = (img.positionX || 0) + deltaX;
            ny = (img.positionY || 0) + deltaY;
          } else if (container) {
            const rect = container.getBoundingClientRect();
            const cx = rect.width / 2;
            const cy = rect.height / 2;
            const ds = dragStartPosRef.current;
            nx = clientX - rect.left - cx - ds.x;
            ny = clientY - rect.top - cy - ds.y;
          } else {
            nx = (img.positionX || 0) + deltaX;
            ny = (img.positionY || 0) + deltaY;
          }
          const updated = placeImage(images, draggingImageIndex, nx, ny);
          return updated ? { ...prev, welcomeScreenImages: updated } : prev;
        });
        setHasChanges(true);
        return;
      }

      if (draggingTextElement) {
        setSettings((prev) => {
          const applyTextAbsolute = (nx, ny) => {
            if (welcomeScreenEditorVariantRef.current === 'mobile') {
              const mob = { ...(prev.welcomeScreenMobile || {}) };
              if (draggingTextElement === 'text') {
                mob.welcomeScreenTextPositionX = Math.round(nx);
                mob.welcomeScreenTextPositionY = Math.round(ny);
              } else if (draggingTextElement === 'subtitle') {
                mob.welcomeScreenSubtitlePositionX = Math.round(nx);
                mob.welcomeScreenSubtitlePositionY = Math.round(ny);
              } else if (draggingTextElement === 'description') {
                mob.welcomeScreenDescriptionPositionX = Math.round(nx);
                mob.welcomeScreenDescriptionPositionY = Math.round(ny);
              }
              return { ...prev, welcomeScreenMobile: mob };
            }
            const next = { ...prev };
            if (draggingTextElement === 'text') {
              next.welcomeScreenTextPositionX = Math.round(nx);
              next.welcomeScreenTextPositionY = Math.round(ny);
            } else if (draggingTextElement === 'subtitle') {
              next.welcomeScreenSubtitlePositionX = Math.round(nx);
              next.welcomeScreenSubtitlePositionY = Math.round(ny);
            } else if (draggingTextElement === 'description') {
              next.welcomeScreenDescriptionPositionX = Math.round(nx);
              next.welcomeScreenDescriptionPositionY = Math.round(ny);
            }
            return next;
          };

          let nx;
          let ny;
          if (isFs) {
            let cx = 0;
            let cy = 0;
            if (draggingTextElement === 'text') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenTextPositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenTextPositionY') || 0;
            } else if (draggingTextElement === 'subtitle') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenSubtitlePositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenSubtitlePositionY') || 0;
            } else if (draggingTextElement === 'description') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenDescriptionPositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenDescriptionPositionY') || 0;
            }
            nx = cx + deltaX;
            ny = cy + deltaY;
          } else if (container) {
            const rect = container.getBoundingClientRect();
            const ccx = rect.width / 2;
            const ccy = rect.height / 2;
            const dts = dragTextStartPosRef.current;
            nx = clientX - rect.left - ccx - dts.x;
            ny = clientY - rect.top - ccy - dts.y;
          } else {
            let cx = 0;
            let cy = 0;
            if (draggingTextElement === 'text') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenTextPositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenTextPositionY') || 0;
            } else if (draggingTextElement === 'subtitle') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenSubtitlePositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenSubtitlePositionY') || 0;
            } else if (draggingTextElement === 'description') {
              cx = getEffectiveWelcomeField(prev, 'welcomeScreenDescriptionPositionX') || 0;
              cy = getEffectiveWelcomeField(prev, 'welcomeScreenDescriptionPositionY') || 0;
            }
            nx = cx + deltaX;
            ny = cy + deltaY;
          }
          return applyTextAbsolute(nx, ny);
        });
        setHasChanges(true);
      }
    };

    const handleWindowPointerMove = (e) => {
      try {
        e.preventDefault();
      } catch (err) {
        /* ignore */
      }
      const { x: clientX, y: clientY } = getClientXY(e);
      processMove(clientX, clientY, e.movementX, e.movementY);
    };

    const handleWindowPointerUp = (ev) => {
      endPreviewDrag(ev);
    };

    // capture: true — иначе onPointerMove на [data-preview-container] со stopPropagation
    // не даёт событию дойти до window в фазе всплытия (мышь и перо перестают двигать слои).
    const winMoveOpts = { passive: false, capture: true };
    const winUpOpts = { capture: true };
    window.addEventListener('pointermove', handleWindowPointerMove, winMoveOpts);
    window.addEventListener('pointerup', handleWindowPointerUp, winUpOpts);
    window.addEventListener('pointercancel', handleWindowPointerUp, winUpOpts);
    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove, winMoveOpts);
      window.removeEventListener('pointerup', handleWindowPointerUp, winUpOpts);
      window.removeEventListener('pointercancel', handleWindowPointerUp, winUpOpts);
    };
  }, [draggingImageIndex, draggingTextElement, endPreviewDrag]);

  // Pinch-to-zoom на превью приветствия (текст / картинки), passive: false
  useEffect(() => {
    if (!open || activeTab !== 'welcome') return;
    const root = document.querySelector('.app-title-settings-modal');
    if (!root) return;
    const containers = root.querySelectorAll('[data-preview-container]');
    if (!containers.length) return;

    const applyPinchFromTouches = (t0, t1) => {
      const st = pinchStateRef.current;
      if (!st) return;
      const newDist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      if (newDist < 8 || st.startDist < 8) return;
      const ratio = newDist / st.startDist;
      const preview = previewWelcomeSettingsRef.current;
      if (!preview) return;

      if (st.kind === 'text') {
        const next = Math.max(50, Math.min(200, Math.round(st.baseScale * ratio)));
        patchWelcomeKey('welcomeScreenTextScale', next);
        return;
      }
      if (st.kind === 'subtitle') {
        const next = Math.max(50, Math.min(200, Math.round(st.baseSubtitleScale * ratio)));
        patchWelcomeKey('welcomeScreenSubtitleScale', next);
        return;
      }
      if (st.kind === 'description') {
        const next = Math.max(50, Math.min(200, Math.round(st.baseDescScale * ratio)));
        patchWelcomeKey('welcomeScreenDescriptionScale', next);
        return;
      }
      if (st.kind === 'image' && st.imageIndex != null) {
        const images = [...(preview.welcomeScreenImages || [])];
        const img = images[st.imageIndex];
        if (!img) return;
        if (st.isPhoto) {
          const next = Math.max(50, Math.min(400, Math.round(st.basePhotoScale * ratio)));
          images[st.imageIndex] = { ...img, imageDisplayScale: next };
        } else {
          const w = Math.max(50, Math.min(500, Math.round(st.baseW * ratio)));
          const h = Math.max(50, Math.min(500, Math.round(st.baseH * ratio)));
          images[st.imageIndex] = { ...img, width: w, height: h };
        }
        patchWelcomeKey('welcomeScreenImages', images);
      }
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 2) return;
      try {
        e.preventDefault();
      } catch (_) {
        /* ignore */
      }
      endPreviewDrag();
      const container = e.currentTarget;
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const cx = (t0.clientX + t1.clientX) / 2;
      const cy = (t0.clientY + t1.clientY) / 2;
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const preview = previewWelcomeSettingsRef.current;
      if (!preview || dist < 12) return;

      const targetKey = resolveWelcomePinchTarget(cx, cy, container);
      if (!targetKey) return;

      if (targetKey === 'text') {
        pinchStateRef.current = {
          kind: 'text',
          startDist: dist,
          baseScale: preview.welcomeScreenTextScale || 100,
        };
        return;
      }
      if (targetKey === 'subtitle') {
        pinchStateRef.current = {
          kind: 'subtitle',
          startDist: dist,
          baseSubtitleScale: preview.welcomeScreenSubtitleScale || 100,
        };
        return;
      }
      if (targetKey === 'description') {
        pinchStateRef.current = {
          kind: 'description',
          startDist: dist,
          baseDescScale: preview.welcomeScreenDescriptionScale || 100,
        };
        return;
      }
      if (targetKey.startsWith('image-')) {
        const idx = parseInt(targetKey.slice('image-'.length), 10);
        if (Number.isNaN(idx)) return;
        const img = (preview.welcomeScreenImages || [])[idx];
        if (!img) return;
        const isPhoto = img.mediaType !== 'video';
        if (isPhoto) {
          pinchStateRef.current = {
            kind: 'image',
            imageIndex: idx,
            startDist: dist,
            isPhoto: true,
            basePhotoScale: img.imageDisplayScale ?? 100,
          };
        } else {
          pinchStateRef.current = {
            kind: 'image',
            imageIndex: idx,
            startDist: dist,
            isPhoto: false,
            baseW: img.width || 100,
            baseH: img.height || 100,
          };
        }
      }
    };

    const onTouchMove = (e) => {
      if (!pinchStateRef.current || e.touches.length < 2) return;
      try {
        e.preventDefault();
      } catch (_) {
        /* ignore */
      }
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      applyPinchFromTouches(t0, t1);
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStateRef.current = null;
    };

    const optsCapture = { capture: true };
    containers.forEach((c) => {
      c.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
      c.addEventListener('touchmove', onTouchMove, { passive: false });
      c.addEventListener('touchend', onTouchEnd, optsCapture);
      c.addEventListener('touchcancel', onTouchEnd, optsCapture);
    });

    return () => {
      pinchStateRef.current = null;
      containers.forEach((c) => {
        c.removeEventListener('touchstart', onTouchStart, { capture: true });
        c.removeEventListener('touchmove', onTouchMove);
        c.removeEventListener('touchend', onTouchEnd, optsCapture);
        c.removeEventListener('touchcancel', onTouchEnd, optsCapture);
      });
    };
  }, [open, activeTab, showFullscreenPreview, patchWelcomeKey, endPreviewDrag]);

  const setWelcomeTextPosition = (elementType, x, y) => {
    setSettings((prev) => {
      if (welcomeScreenEditorVariantRef.current === 'mobile') {
        const mob = { ...(prev.welcomeScreenMobile || {}) };
        if (elementType === 'text') {
          mob.welcomeScreenTextPositionX = Math.round(x);
          mob.welcomeScreenTextPositionY = Math.round(y);
        } else if (elementType === 'subtitle') {
          mob.welcomeScreenSubtitlePositionX = Math.round(x);
          mob.welcomeScreenSubtitlePositionY = Math.round(y);
        } else if (elementType === 'description') {
          mob.welcomeScreenDescriptionPositionX = Math.round(x);
          mob.welcomeScreenDescriptionPositionY = Math.round(y);
        }
        return { ...prev, welcomeScreenMobile: mob };
      }
      const next = { ...prev };
      if (elementType === 'text') {
        next.welcomeScreenTextPositionX = Math.round(x);
        next.welcomeScreenTextPositionY = Math.round(y);
      } else if (elementType === 'subtitle') {
        next.welcomeScreenSubtitlePositionX = Math.round(x);
        next.welcomeScreenSubtitlePositionY = Math.round(y);
      } else if (elementType === 'description') {
        next.welcomeScreenDescriptionPositionX = Math.round(x);
        next.welcomeScreenDescriptionPositionY = Math.round(y);
      }
      return next;
    });
    setHasChanges(true);
  };

  // Обработчик масштабирования изображения скроллингом
  const handleImageWheel = (e, index) => {
    // Предотвращаем прокрутку и всплытие
    e.stopPropagation();
    // preventDefault может не работать в пассивных слушателях, но это нормально
    try {
      e.preventDefault();
    } catch (err) {
      // Игнорируем ошибку пассивного слушателя
    }
    const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
    const img = newImages[index];
    if (!img) return;

    const isPhoto = img.mediaType !== 'video';
    if (isPhoto) {
      const deltaScale = e.deltaY > 0 ? -5 : 5;
      const next = Math.max(50, Math.min(400, (img.imageDisplayScale ?? 100) + deltaScale));
      newImages[index] = { ...img, imageDisplayScale: next };
    } else {
      const delta = e.deltaY > 0 ? -10 : 10;
      const newWidth = Math.max(50, Math.min(500, (img.width || 100) + delta));
      const newHeight = Math.max(50, Math.min(500, (img.height || 100) + delta));
      newImages[index] = { ...img, width: newWidth, height: newHeight };
    }
    patchWelcomeKey('welcomeScreenImages', newImages);
  };

  const handleTextPointerDown = (e, elementType) => {
    e.preventDefault();
    e.stopPropagation();
    const previewContainer = e.currentTarget.closest('[data-preview-container]');
    if (!previewContainer) return;
    activePreviewContainerRef.current = previewContainer;
    fullscreenDragRef.current = previewContainer.dataset?.fullscreenPreview === 'true';
    if (typeof e.pointerId === 'number' && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
    }
    const { x, y } = getClientXY(e);
    setDraggingTextElement(elementType);
    dragLastPosRef.current = { x, y };
    const previewRect = previewContainer.getBoundingClientRect();

    let currentPositionX = 0;
    let currentPositionY = 0;
    if (elementType === 'text') {
      currentPositionX = previewWelcomeSettings.welcomeScreenTextPositionX || 0;
      currentPositionY = previewWelcomeSettings.welcomeScreenTextPositionY || 0;
    } else if (elementType === 'subtitle') {
      currentPositionX = previewWelcomeSettings.welcomeScreenSubtitlePositionX || 0;
      currentPositionY = previewWelcomeSettings.welcomeScreenSubtitlePositionY || 0;
    } else if (elementType === 'description') {
      currentPositionX = previewWelcomeSettings.welcomeScreenDescriptionPositionX || 0;
      currentPositionY = previewWelcomeSettings.welcomeScreenDescriptionPositionY || 0;
    }

    const centerX = previewRect.width / 2;
    const centerY = previewRect.height / 2;
    const currentElementRelativeX = centerX + currentPositionX;
    const currentElementRelativeY = centerY + currentPositionY;

    const pointerRelX = x - previewRect.left;
    const pointerRelY = y - previewRect.top;

    const textStart = {
      x: pointerRelX - currentElementRelativeX,
      y: pointerRelY - currentElementRelativeY,
    };
    dragTextStartPosRef.current = textStart;
    setDragTextStartPos(textStart);
  };

  const bumpWelcomeImageScale = (index, delta) => {
    const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
    const img = newImages[index];
    if (!img) return;
    const isPhoto = img.mediaType !== 'video';
    if (isPhoto) {
      const next = Math.max(50, Math.min(400, (img.imageDisplayScale ?? 100) + delta));
      newImages[index] = { ...img, imageDisplayScale: next };
    } else {
      const newWidth = Math.max(50, Math.min(500, (img.width || 100) + delta));
      const newHeight = Math.max(50, Math.min(500, (img.height || 100) + delta));
      newImages[index] = { ...img, width: newWidth, height: newHeight };
    }
    patchWelcomeKey('welcomeScreenImages', newImages);
  };

  // Обработчик масштабирования текста скроллингом
  const handleTextWheel = (e, elementType) => {
    // Предотвращаем всплытие
    e.stopPropagation();
    // preventDefault может не работать в пассивных слушателях, но это нормально
    try {
      e.preventDefault();
    } catch (err) {
      // Игнорируем ошибку пассивного слушателя
    }
    const delta = e.deltaY > 0 ? -5 : 5;
    
    if (elementType === 'text') {
      const currentScale = previewWelcomeSettings.welcomeScreenTextScale || 100;
      const newScale = Math.max(50, Math.min(200, currentScale + delta));
      patchWelcomeKey('welcomeScreenTextScale', newScale);
    } else if (elementType === 'subtitle') {
      const currentScale = previewWelcomeSettings.welcomeScreenSubtitleScale || 100;
      const newScale = Math.max(50, Math.min(200, currentScale + delta));
      patchWelcomeKey('welcomeScreenSubtitleScale', newScale);
    } else if (elementType === 'description') {
      const currentScale = previewWelcomeSettings.welcomeScreenDescriptionScale || 100;
      const newScale = Math.max(50, Math.min(200, currentScale + delta));
      patchWelcomeKey('welcomeScreenDescriptionScale', newScale);
    }
  };

  // Функции для rich text editor описания
  const saveDescriptionEditorSelection = useCallback(() => {
    const root = descriptionEditorRef.current;
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!root || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;
    try {
      descriptionSavedRangeRef.current = range.cloneRange();
    } catch (e) {
      descriptionSavedRangeRef.current = null;
    }
  }, []);

  const restoreDescriptionSavedRange = useCallback(() => {
    const root = descriptionEditorRef.current;
    const r = descriptionSavedRangeRef.current;
    if (!root || !r) return false;
    try {
      root.focus({ preventScroll: true });
    } catch (e) {
      root.focus();
    }
    const sel = window.getSelection();
    sel.removeAllRanges();
    try {
      sel.addRange(r);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const applyDescriptionRangeInlineStyle = useCallback((stylePatch) => {
    const root = descriptionEditorRef.current;
    const saved = descriptionSavedRangeRef.current;
    if (!root || !saved || saved.collapsed) return false;
    if (!root.contains(saved.commonAncestorContainer)) return false;
    let r;
    try {
      r = saved.cloneRange();
    } catch (e) {
      return false;
    }
    const span = document.createElement('span');
    Object.assign(span.style, stylePatch);
    try {
      r.surroundContents(span);
    } catch (e) {
      try {
        const frag = r.extractContents();
        span.appendChild(frag);
        r.insertNode(span);
      } catch (e2) {
        return false;
      }
    }
    const sel = window.getSelection();
    sel.removeAllRanges();
    const nr = document.createRange();
    nr.selectNodeContents(span);
    nr.collapse(false);
    sel.addRange(nr);
    try {
      descriptionSavedRangeRef.current = nr.cloneRange();
    } catch (e) {
      descriptionSavedRangeRef.current = null;
    }
    return true;
  }, []);

  const handleDescriptionToolbarPointerDownCapture = useCallback(
    (e) => {
      const ed = descriptionEditorRef.current;
      if (!ed) return;
      if (ed === e.target || ed.contains(e.target)) return;
      saveDescriptionEditorSelection();
    },
    [saveDescriptionEditorSelection]
  );

  const execDescriptionFormat = (cmd, value = null) => {
    const ed = descriptionEditorRef.current;
    if (!ed) return;
    restoreDescriptionSavedRange();
    try {
      document.execCommand('styleWithCSS', false, 'true');
    } catch (e) {
      /* Safari/WebKit */
    }
    const htmlBefore = ed.innerHTML;
    document.execCommand(cmd, false, value);
    if (
      (cmd === 'foreColor' || cmd === 'hiliteColor') &&
      value != null &&
      htmlBefore === ed.innerHTML &&
      descriptionSavedRangeRef.current &&
      !descriptionSavedRangeRef.current.collapsed
    ) {
      applyDescriptionRangeInlineStyle(
        cmd === 'foreColor' ? { color: value } : { backgroundColor: value }
      );
    }
    patchWelcomeKey('welcomeScreenDescriptionText', ed.innerHTML);
  };

  const insertCustomEmojiAtCursor = (token) => {
    if (!descriptionEditorRef.current) return;
    descriptionEditorRef.current.focus();
    const url = customEmojiMap[token];
    if (!url) {
      document.execCommand('insertText', false, token);
      const currentHtml = descriptionEditorRef.current ? descriptionEditorRef.current.innerHTML : '';
      patchWelcomeKey('welcomeScreenDescriptionText', currentHtml);
      return;
    }
    const img = document.createElement('img');
    img.src = url;
    img.alt = token;
    img.setAttribute('data-custom-emoji', 'true');
    img.setAttribute('data-token', token);
    const px = Number(emojiSizes.welcome || 24);
    img.style.width = px + 'px';
    img.style.height = px + 'px';
    img.style.objectFit = 'cover';
    img.style.verticalAlign = 'middle';
    img.style.margin = '0 2px';
    img.style.borderRadius = '6px';
    const range = window.getSelection && window.getSelection().getRangeAt && window.getSelection().rangeCount > 0 ? window.getSelection().getRangeAt(0) : null;
    if (range) {
      range.deleteContents();
      range.insertNode(img);
      range.setStartAfter(img);
      range.setEndAfter(img);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      descriptionEditorRef.current.appendChild(img);
    }
    const currentHtml = descriptionEditorRef.current ? descriptionEditorRef.current.innerHTML : '';
    patchWelcomeKey('welcomeScreenDescriptionText', currentHtml);
  };

  // Обработка загрузки изображения аватарки
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Проверяем размер файла (максимум 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Файл слишком большой. Максимальный размер: 5MB. Файл будет сжат после загрузки.');
      }
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // Сжимаем изображение сразу после загрузки
          const compressed = await compressImage(reader.result, 300, 300, 0.75);
          handleChange('avatarImage', compressed);
        } catch (error) {
          console.error('Ошибка при сжатии изображения аватарки:', error);
          alert('Ошибка при обработке изображения. Попробуйте другое изображение.');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Обработка загрузки изображения снеговика
  const handleSnowmanImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Проверяем размер файла (максимум 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Файл слишком большой. Максимальный размер: 5MB. Файл будет сжат после загрузки.');
      }
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // Сжимаем изображение сразу после загрузки
          const compressed = await compressImage(reader.result, 300, 300, 0.75);
          handleChange('snowmanImage', compressed);
        } catch (error) {
          console.error('Ошибка при сжатии изображения снеговика:', error);
          alert('Ошибка при обработке изображения. Попробуйте другое изображение.');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Сохранение пресета
  const handleSavePreset = () => {
    if (!presetName.trim()) {
      alert('Введите название пресета');
      return;
    }

    // Сохраняем только настройки текста (без изображений)
    const textSettings = {
      text: settings.text,
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      customFontUrl: settings.customFontUrl,
      customFontName: settings.customFontName,
      color: settings.color,
      useGradient: settings.useGradient,
      gradientStart: settings.gradientStart,
      gradientEnd: settings.gradientEnd,
      glowEnabled: settings.glowEnabled,
      glowColor: settings.glowColor,
      glowIntensity: settings.glowIntensity,
      glowSpread: settings.glowSpread,
      effectType: settings.effectType
    };

    const preset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      settings: textSettings,
      createdAt: new Date().toISOString()
    };

    if (savePreset(preset)) {
      setPresets(getPresets());
      setPresetName('');
      setShowPresetInput(false);
      alert(`Пресет "${preset.name}" сохранен!`);
    } else {
      alert('Ошибка при сохранении пресета');
    }
  };

  // Применение пресета
  const handleApplyPreset = (preset) => {
    const newSettings = {
      ...settings,
      ...preset.settings
    };
    setSettings(newSettings);
    setHasChanges(true);
    alert(`Пресет "${preset.name}" применен!`);
  };

  // Удаление пресета
  const handleDeletePreset = (presetId, presetName) => {
    if (window.confirm(`Удалить пресет "${presetName}"?`)) {
      if (deletePreset(presetId)) {
        setPresets(getPresets());
        alert('Пресет удален');
      } else {
        alert('Ошибка при удалении пресета');
      }
    }
  };

  const handleSave = async () => {
    try {
      // Убеждаемся, что все значения правильно сохранены
      const settingsToSave = {
        ...settings,
        useGradient: settings.useGradient === true,
        glowEnabled: settings.glowEnabled === true,
        glowIntensity: Number(settings.glowIntensity) || 12,
        glowSpread: Number(settings.glowSpread) || 32,
      };

      const gfUnion = [
        ...new Set([
          ...(Array.isArray(settingsToSave.welcomeScreenGoogleFontFamilies)
            ? settingsToSave.welcomeScreenGoogleFontFamilies
            : []),
          ...(Array.isArray(settingsToSave.welcomeScreenMobile?.welcomeScreenGoogleFontFamilies)
            ? settingsToSave.welcomeScreenMobile.welcomeScreenGoogleFontFamilies
            : []),
        ]),
      ].filter(Boolean);
      settingsToSave.welcomeScreenGoogleFontFamilies = gfUnion;
      settingsToSave.welcomeScreenMobile = {
        ...(settingsToSave.welcomeScreenMobile || {}),
        welcomeScreenGoogleFontFamilies: [...gfUnion],
      };

      // Сжимаем изображения перед сохранением, если они еще не сжаты
      if (settingsToSave.avatarImage && settingsToSave.avatarImage.startsWith('data:image')) {
        try {
          settingsToSave.avatarImage = await compressImage(settingsToSave.avatarImage, 300, 300, 0.75);
        } catch (error) {
          console.error('Ошибка при сжатии аватарки:', error);
        }
      }
      
      if (settingsToSave.snowmanImage && settingsToSave.snowmanImage.startsWith('data:image')) {
        try {
          settingsToSave.snowmanImage = await compressImage(settingsToSave.snowmanImage, 300, 300, 0.75);
        } catch (error) {
          console.error('Ошибка при сжатии снеговика:', error);
        }
      }

      const compressWelcomeImagesList = async (list) => {
        if (!Array.isArray(list)) return list;
        const out = [];
        for (const item of list) {
          if (!item?.image || typeof item.image !== 'string' || !item.image.startsWith('data:image')) {
            out.push(item);
            continue;
          }
          try {
            const compressed = await compressImage(item.image, 500, 500, 0.75);
            out.push({ ...item, image: compressed });
          } catch (error) {
            console.error('Ошибка при сжатии медиа приветствия:', error);
            out.push(item);
          }
        }
        return out;
      };
      if (Array.isArray(settingsToSave.welcomeScreenImages)) {
        settingsToSave.welcomeScreenImages = await compressWelcomeImagesList(settingsToSave.welcomeScreenImages);
      }
      if (settingsToSave.welcomeScreenMobile && Array.isArray(settingsToSave.welcomeScreenMobile.welcomeScreenImages)) {
        settingsToSave.welcomeScreenMobile = {
          ...settingsToSave.welcomeScreenMobile,
          welcomeScreenImages: await compressWelcomeImagesList(settingsToSave.welcomeScreenMobile.welcomeScreenImages),
        };
      }
      
      // console.log('Saving settings:', settingsToSave);
      console.log('💾 [AppTitleSettingsModal] Сохраняем настройки:', {
        customFontName: settingsToSave.customFontName || 'Missing',
        customFontDataUrl: settingsToSave.customFontDataUrl ? 'Present (' + (settingsToSave.customFontDataUrl.length / 1024).toFixed(2) + ' KB)' : 'Missing',
        fontFamily: settingsToSave.fontFamily
      });
      setAppTitleSettings(settingsToSave);
      setHasChanges(false);
      
      // Сохраняем на сервере для синхронизации между всеми пользователями
      try {
        console.log('📤 Отправка настроек названия приложения на сервер...');
        console.log('📤 [AppTitleSettingsModal] customFontDataUrl в отправляемых настройках:', settingsToSave.customFontDataUrl ? 'Present' : 'Missing');
        const response = await api.post('/api/app-title-settings', { settings: settingsToSave });
        if (response.data.success) {
          console.log('✅ Настройки названия приложения сохранены и синхронизированы со всеми пользователями!');
          alert('Настройки названия приложения сохранены и синхронизированы со всеми пользователями!');
        } else {
          console.warn('⚠️ Настройки сохранены локально, но не удалось синхронизировать с сервером');
          alert('Настройки сохранены локально, но не удалось синхронизировать с сервером.');
        }
      } catch (error) {
        console.error('❌ Ошибка синхронизации настроек с сервером:', error);
        // Показываем предупреждение, но не блокируем сохранение
        if (error.response?.status === 403) {
          alert('Настройки сохранены локально. Для синхронизации с другими пользователями необходимы права администратора.');
        } else {
          alert('Настройки сохранены локально, но не удалось синхронизировать с сервером. Проверьте подключение к интернету.');
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      alert('Ошибка при сохранении настроек. Попробуйте еще раз или удалите изображения.');
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(true);
  };

  // Загрузка кастомного шрифта для превью (URL или локальный файл)
  useEffect(() => {
    console.log('🔤 [AppTitleSettingsModal] Применяем кастомный шрифт:', {
      customFontUrl: settings.customFontUrl ? 'Present' : 'Missing',
      customFontDataUrl: settings.customFontDataUrl ? 'Present (' + (settings.customFontDataUrl.length / 1024).toFixed(2) + ' KB)' : 'Missing',
      customFontName: settings.customFontName || 'Missing'
    });
    
    // Удаляем старые динамические теги, чтобы не плодить дубликаты
    if (settings.customFontUrl) {
      const existingLink = document.querySelector(`link[data-custom-font="${settings.customFontUrl}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = settings.customFontUrl;
        link.setAttribute('data-custom-font', settings.customFontUrl);
        document.head.appendChild(link);
        console.log('✅ [AppTitleSettingsModal] Загружен шрифт по URL:', settings.customFontUrl);
      }
    }
    // Для локального файла (dataUrl)
    if (settings.customFontDataUrl && settings.customFontName) {
      // Удаляем предыдущий style с этим шрифтом
      const prevStyle = document.getElementById('custom-font-style');
      if (prevStyle) {
        prevStyle.remove();
        console.log('🗑️ [AppTitleSettingsModal] Удален предыдущий @font-face');
      }
      // Добавляем новый style с @font-face
      const style = document.createElement('style');
      style.id = 'custom-font-style';
      style.innerHTML = `@font-face { font-family: '${settings.customFontName}'; src: url('${settings.customFontDataUrl}'); font-display: swap; }`;
      document.head.appendChild(style);
      console.log('✅ [AppTitleSettingsModal] Применен кастомный шрифт через @font-face:', settings.customFontName);
    }
    // Если убрали кастомный шрифт — чистим style
    if (!settings.customFontDataUrl && !settings.customFontUrl) {
      const prevStyle = document.getElementById('custom-font-style');
      if (prevStyle) {
        prevStyle.remove();
        console.log('🗑️ [AppTitleSettingsModal] Удален @font-face (шрифт не используется)');
      }
    }
  }, [settings.customFontUrl, settings.customFontDataUrl, settings.customFontName]);

  // Генерация стилей для превью
  const getPreviewStyle = () => {
    // Используем fontFamily напрямую - он уже содержит правильное значение
    // (либо кастомный шрифт, либо выбранный из списка)
    const fontFamilyValue = settings.fontFamily || 'Arial, sans-serif';
    
    const style = {
      fontSize: settings.fontSize || '2em',
      fontFamily: fontFamilyValue,
      marginTop: 2,
      marginLeft: -50,
    };

    // Применяем градиент (если не используется градиентная анимация)
    const effectType = settings.effectType || 'neon';
    if (settings.useGradient === true && effectType !== 'gradient-animation') {
      const gradientStart = settings.gradientStart || '#43e97b';
      const gradientEnd = settings.gradientEnd || '#2193b0';
      style.background = `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`;
      style.WebkitBackgroundClip = 'text';
      style.WebkitTextFillColor = 'transparent';
      style.backgroundClip = 'text';
      style.color = 'transparent';
    } else if (effectType === 'gradient-animation' && settings.glowEnabled) {
      // Градиентная анимация будет применена через CSS класс
      style.color = 'transparent';
    } else {
      style.color = settings.color || '#43e97b';
      // Убираем градиентные свойства
      style.background = 'none';
      style.WebkitBackgroundClip = 'unset';
      style.WebkitTextFillColor = 'unset';
      style.backgroundClip = 'unset';
    }

    // Применяем эффекты в зависимости от выбранного типа
    const glowColor = settings.glowColor ? String(settings.glowColor).trim() : '#43e97b';
    const intensity = Number(settings.glowIntensity) || 12;
    const spread = Number(settings.glowSpread) || 32;

    if (settings.glowEnabled === true) {
      switch (effectType) {
        case 'shadow':
          // Эффект тени
          style.textShadow = `3px 3px 6px rgba(0,0,0,0.5), 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
          break;
        case 'outline':
          // Эффект обводки
          style.textShadow = `
            -1px -1px 0 ${glowColor},
            1px -1px 0 ${glowColor},
            -1px 1px 0 ${glowColor},
            1px 1px 0 ${glowColor},
            0 0 ${intensity}px ${glowColor},
            0 0 ${spread}px ${glowColor}
          `.replace(/\s+/g, ' ').trim();
          break;
        case 'sparkle':
          // Эффект искр
          style.textShadow = `
            0 0 ${intensity}px ${glowColor},
            0 0 ${spread}px ${glowColor},
            0 0 ${spread * 1.5}px ${glowColor},
            2px 2px 4px rgba(0,0,0,0.3),
            -2px -2px 4px ${glowColor}88
          `.replace(/\s+/g, ' ').trim();
          break;
        case 'gradient-animation':
          // Градиентная анимация (будет через CSS)
          style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
          break;
        case 'new-year':
          // Новогодняя анимация (будет через CSS)
          style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
          break;
        case 'neon':
        default:
          // Неоновое свечение (будет через анимацию)
          // Не устанавливаем textShadow здесь, анимация будет управлять им
          break;
      }
    } else {
      style.textShadow = 'none';
    }

    return style;
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: compactModalLayout ? 0 : '320px',
        right: 0,
        bottom: 0,
        width: compactModalLayout ? '100vw' : 'calc(100vw - 320px)',
        maxWidth: compactModalLayout ? '100%' : undefined,
        minHeight: '100dvh',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        zIndex: 200004,
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      }}
    >
      <div
        className={compactModalLayout ? 'app-title-settings-modal app-title-settings-modal--compact' : 'app-title-settings-modal'}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          minWidth: '100%',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          color: '#fff',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box'
        }}
      >
        {compactModalLayout && (
          <style>{`
            .app-title-settings-modal--compact input[type="text"],
            .app-title-settings-modal--compact input[type="search"],
            .app-title-settings-modal--compact input[type="number"],
            .app-title-settings-modal--compact select,
            .app-title-settings-modal--compact textarea {
              font-size: 16px !important;
            }
          `}</style>
        )}
        {/* Фиксированный заголовок */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: '0',
          right: '0',
          width: '100%',
          maxWidth: '100%',
          height: compactModalLayout ? 52 : 64,
          backgroundColor: 'rgba(35, 41, 49, 0.95)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: compactModalLayout ? '0 12px' : '0 32px',
          zIndex: 1000000,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pointerEvents: 'auto',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: compactModalLayout ? '1.05em' : '1.8em', color: '#a3e635', display: 'flex', alignItems: 'center', gap: compactModalLayout ? 6 : 10, lineHeight: 1.2 }}>
            <FiType size={compactModalLayout ? 20 : 24} /> {compactModalLayout ? 'Название приложения' : 'Управление названием приложения'}
          </h2>
          <div style={{ position: 'relative', width: 52, flexShrink: 0 }}>
            <ModalCloseButton onClick={onClose} aria-label="Закрыть" />
          </div>
        </div>

        {/* Вкладки */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: compactModalLayout ? '10px 12px' : '16px 32px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(35, 41, 49, 0.5)',
          marginTop: compactModalLayout ? 52 : 64
        }}>
          <button
            onClick={() => setActiveTab('sidebar')}
            style={{
              padding: compactModalLayout ? '10px 14px' : '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'sidebar' ? 'rgba(109, 213, 237, 0.2)' : 'transparent',
              color: activeTab === 'sidebar' ? '#6dd5ed' : '#9ca3af',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: compactModalLayout ? '0.88em' : '1em',
              transition: 'all 0.2s',
              flex: compactModalLayout ? '1 1 140px' : '0 0 auto',
            }}
          >
            📋 {compactModalLayout ? 'Сайдбар' : 'Настройки сайдбара'}
          </button>
          <button
            onClick={() => setActiveTab('welcome')}
            style={{
              padding: compactModalLayout ? '10px 14px' : '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'welcome' ? 'rgba(163, 230, 53, 0.2)' : 'transparent',
              color: activeTab === 'welcome' ? '#a3e635' : '#9ca3af',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: compactModalLayout ? '0.88em' : '1em',
              transition: 'all 0.2s',
              flex: compactModalLayout ? '1 1 140px' : '0 0 auto',
            }}
          >
            🖥️ {compactModalLayout ? 'Приветствие' : 'Настройки приветственного экрана'}
          </button>
        </div>

        {/* Контент с прокруткой */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          padding: compactModalLayout ? '12px 14px 24px' : '32px 48px',
          marginTop: compactModalLayout ? 52 : 64,
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box'
        }}>
        {/* Контейнер с двумя колонками: настройки слева, превью справа */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: compactModalLayout ? 'minmax(0, 1fr)' : '1fr 400px',
          gap: compactModalLayout ? '16px' : '24px',
          alignItems: 'flex-start',
        }}>
          {/* Настройки */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: compactModalLayout ? '14px' : '20px', paddingRight: compactModalLayout ? 0 : '8px', minWidth: 0 }}>
          
          {activeTab === 'sidebar' && (
            <>
          {/* Настройки сайдбара */}
          <div style={{ 
            padding: '16px', 
            background: 'rgba(67, 233, 123, 0.08)', 
            borderRadius: '12px', 
            border: '1px solid rgba(67, 233, 123, 0.2)',
            marginBottom: '8px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '16px',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#43e97b'
            }}>
              <span>⚙️</span>
              <span>Общие настройки</span>
              <span style={{ 
                fontSize: '0.75em', 
                fontWeight: 500, 
                color: '#9ca3af',
                marginLeft: 'auto',
                padding: '4px 8px',
                background: 'rgba(67, 233, 123, 0.15)',
                borderRadius: '6px'
              }}>
                Сайдбар + Приветственный экран
              </span>
            </div>
            
          {/* Текст */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Текст названия
            </label>
            <input
              type="text"
              value={settings.text}
              onChange={(e) => handleChange('text', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #2f3440',
                background: '#0e1420',
                color: '#fff',
                fontSize: '1em'
              }}
              placeholder="Введите название приложения"
            />
          </div>

          {/* Размер шрифта */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Размер шрифта
            </label>
            <input
              type="text"
              value={settings.fontSize}
              onChange={(e) => handleChange('fontSize', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #2f3440',
                background: '#0e1420',
                color: '#fff',
                fontSize: '1em'
              }}
              placeholder="Например: 2em, 32px, 2rem"
            />
            <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
              Примеры: 1.5em, 24px, 2rem
            </div>
          </div>

          {/* Шрифт */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Шрифт
            </label>
            {/* Вычисляем базовый fontFamily для select */}
            {(() => {
              const customOptionValue = settings.customFontName && settings.customFontDataUrl
                ? `'${settings.customFontName}', Arial, sans-serif`
                : '';
              
              // selectValue: просто используем текущий fontFamily
              const selectValue = settings.fontFamily || 'Arial, sans-serif';
              
              return (
                <select
                  value={selectValue}
                  onChange={(e) => {
                    // Просто устанавливаем выбранное значение
                    handleChange('fontFamily', e.target.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em',
                    marginBottom: '12px'
                  }}
                >
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                  <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                  <option value="Impact, sans-serif">Impact</option>
                  <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                  {(Array.isArray(settings.sidebarGoogleFontFamilies) ? settings.sidebarGoogleFontFamilies : [])
                    .filter(Boolean)
                    .map((name) => (
                      <option key={`sidebar-gf-${name}`} value={googleFontFamilyCssValue(name)}>
                        {name} (Google Fonts)
                      </option>
                    ))}
                  {/* Кастомный шрифт всегда в конце, если есть */}
                  {settings.customFontName && settings.customFontDataUrl && (
                    <option value={customOptionValue}>{settings.customFontName} (локальный)</option>
                  )}
                </select>
              );
            })()}

            {compactModalLayout && (
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    googleFontsPickerApplyToRef.current = 'sidebar';
                    setGoogleFontsPickerOpen(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(109, 213, 237, 0.45)',
                    background: 'rgba(109, 213, 237, 0.12)',
                    color: '#bae6fd',
                    fontSize: '0.95em',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <FiExternalLink size={18} />
                  Google Fonts — добавить в список
                </button>
                <div style={{ fontSize: '0.8em', color: '#9ca3af', marginTop: '8px', lineHeight: 1.45 }}>
                  Каталог и сайт fonts.google.com. Имя семейства должно совпадать с Google Fonts — затем выберите шрифт в списке выше.
                </div>
                {Array.isArray(settings.sidebarGoogleFontFamilies) && settings.sidebarGoogleFontFamilies.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                    {settings.sidebarGoogleFontFamilies.map((name) => (
                      <span
                        key={name}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: 'rgba(55, 65, 81, 0.9)',
                          color: '#e5e7eb',
                          fontSize: '0.82em',
                        }}
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => removeSidebarGoogleFont(name)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#f87171',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '1.1em',
                            lineHeight: 1,
                          }}
                          aria-label={`Убрать ${name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Загрузка кастомного шрифта (URL или файл) */}
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0', fontSize: '0.95em' }}>
                Загрузить шрифт из интернета или файла (опционально)
              </label>
              <div style={{ fontSize: '0.85em', color: '#9ca3af', marginBottom: '8px' }}>
                Для Google Fonts: https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap
              </div>
              <input
                type="text"
                value={settings.customFontUrl || ''}
                onChange={(e) => handleChange('customFontUrl', e.target.value)}
                placeholder="URL шрифта (CSS файл)"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em',
                  marginBottom: '8px'
                }}
              />
              <div style={{ fontSize: '0.85em', color: '#9ca3af', margin: '8px 0 4px 0' }}>или выберите локальный файл шрифта (ttf, otf, woff, woff2):</div>
              <input
                type="file"
                accept=".ttf,.otf,.woff,.woff2"
                onChange={handleFontFileUpload}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '0.95em',
                  marginBottom: '8px',
                  cursor: 'pointer'
                }}
              />
              {/* Если шрифт уже применён — показать информацию и кнопку сбросить */}
              {settings.customFontDataUrl && settings.customFontName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 8 }}>
                  <span style={{ color: '#43e97b', fontSize: '0.95em' }}>✓ Шрифт применён: {settings.customFontName}</span>
                  <button
                    onClick={() => { 
                      handleChange('customFontDataUrl', ''); 
                      handleChange('customFontName', ''); 
                      handleChange('fontFamily', 'Arial, sans-serif');
                      handleChange('customFontUrl', '');
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e74c3c',
                      background: 'rgba(231,76,60,0.15)',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.85em'
                    }}
                  >
                    Удалить шрифт
                  </button>
                </div>
              )}
              <input
                type="text"
                value={settings.customFontName || ''}
                onChange={(e) => handleChange('customFontName', e.target.value)}
                placeholder="Название шрифта (например: Roboto, Montserrat)"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
              />
              <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                Укажите название шрифта точно так, как оно указано в CSS или в файле
              </div>
            </div>
          </div>

          {/* Использовать градиент */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.useGradient}
                onChange={(e) => handleChange('useGradient', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Использовать градиент
            </label>
          </div>

          {/* Цвет (если градиент выключен) */}
          {!settings.useGradient && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Цвет текста
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={normalizeHexForColorInput(settings.color)}
                  onChange={(e) => handleChange('color', e.target.value)}
                  onInput={(e) => handleChange('color', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={settings.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="#43e97b"
                />
              </div>
            </div>
          )}

          {/* Градиент (если включен) */}
          {settings.useGradient && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                  Начальный цвет градиента
                </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={normalizeHexForColorInput(settings.gradientStart || '#43e97b')}
                      onChange={(e) => handleChange('gradientStart', e.target.value)}
                      onInput={(e) => handleChange('gradientStart', e.target.value)}
                      style={{
                        width: '60px',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={settings.gradientStart || '#43e97b'}
                      onChange={(e) => handleChange('gradientStart', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '1em'
                      }}
                      placeholder="#43e97b"
                    />
                  </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                  Конечный цвет градиента
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={normalizeHexForColorInput(settings.gradientEnd || '#2193b0')}
                    onChange={(e) => handleChange('gradientEnd', e.target.value)}
                    onInput={(e) => handleChange('gradientEnd', e.target.value)}
                    style={{
                      width: '60px',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={settings.gradientEnd || '#2193b0'}
                    onChange={(e) => handleChange('gradientEnd', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em'
                    }}
                    placeholder="#2193b0"
                  />
                  </div>
              </div>
            </>
          )}

          {/* Тип эффекта */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Тип эффекта
            </label>
            <select
              value={settings.effectType || 'neon'}
              onChange={(e) => handleChange('effectType', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #2f3440',
                background: '#0e1420',
                color: '#fff',
                fontSize: '1em'
              }}
            >
              <option value="neon">Неоновое свечение (анимация)</option>
              <option value="shadow">Тень</option>
              <option value="outline">Обводка</option>
              <option value="sparkle">Искры</option>
              <option value="gradient-animation">Градиентная анимация</option>
              <option value="new-year">Новогодняя анимация</option>
            </select>
            <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
              Выберите визуальный эффект для текста
            </div>
          </div>

          {/* Свечение */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={settings.glowEnabled}
                onChange={(e) => handleChange('glowEnabled', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Включить эффект
            </label>
            {settings.glowEnabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginLeft: '26px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Цвет свечения
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={normalizeHexForColorInput(settings.glowColor || '#43e97b')}
                      onChange={(e) => handleChange('glowColor', e.target.value)}
                      onInput={(e) => handleChange('glowColor', e.target.value)}
                      style={{
                        width: '60px',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={settings.glowColor || '#43e97b'}
                      onChange={(e) => handleChange('glowColor', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '1em'
                      }}
                      placeholder="#43e97b"
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Интенсивность свечения: {settings.glowIntensity !== undefined ? settings.glowIntensity : 12}px
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    step="1"
                    value={settings.glowIntensity !== undefined ? settings.glowIntensity : 12}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleChange('glowIntensity', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>1</span>
                    <span>50</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Распространение свечения: {settings.glowSpread || 32}px
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    step="1"
                    value={settings.glowSpread !== undefined ? settings.glowSpread : 32}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleChange('glowSpread', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>0</span>
                    <span>100</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Управление пресетами */}
          <div style={{ marginTop: '24px', padding: '20px', background: 'rgba(67, 233, 123, 0.05)', borderRadius: '12px', border: '1px solid rgba(67, 233, 123, 0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <label style={{ fontWeight: 700, color: '#43e97b', fontSize: '1.1em' }}>
                💾 Пресеты стилей текста
              </label>
              <button
                onClick={() => setShowPresetInput(!showPresetInput)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(67, 233, 123, 0.3)',
                  background: showPresetInput ? 'rgba(67, 233, 123, 0.2)' : 'transparent',
                  color: '#43e97b',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9em',
                  transition: 'all 0.2s'
                }}
              >
                {showPresetInput ? '✕ Отмена' : '+ Сохранить пресет'}
              </button>
            </div>

            {showPresetInput && (
              <div style={{ marginBottom: '16px', padding: '16px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Название пресета
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSavePreset();
                      }
                    }}
                    placeholder="Например: Неоновый зеленый"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '0.95em'
                    }}
                  />
                  <button
                    onClick={handleSavePreset}
                    disabled={!presetName.trim()}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(67, 233, 123, 0.5)',
                      background: presetName.trim() ? 'rgba(67, 233, 123, 0.2)' : 'rgba(67, 233, 123, 0.05)',
                      color: presetName.trim() ? '#43e97b' : '#6b7280',
                      cursor: presetName.trim() ? 'pointer' : 'not-allowed',
                      fontWeight: 600,
                      fontSize: '0.95em',
                      transition: 'all 0.2s'
                    }}
                  >
                    Сохранить
                  </button>
                </div>
                <div style={{ fontSize: '0.8em', color: '#9ca3af', marginTop: '8px' }}>
                  Сохранит текущие настройки текста: цвет, градиент, свечение, эффекты, шрифт
                </div>
              </div>
            )}

            {presets.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      border: '1px solid rgba(67, 233, 123, 0.1)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: '4px' }}>
                        {preset.name}
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#9ca3af' }}>
                        {preset.settings.effectType} • {preset.settings.useGradient ? 'Градиент' : preset.settings.color} • {preset.settings.glowEnabled ? 'Свечение' : 'Без свечения'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApplyPreset(preset)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(67, 233, 123, 0.3)',
                          background: 'rgba(67, 233, 123, 0.15)',
                          color: '#43e97b',
                          cursor: 'pointer',
                          fontWeight: 500,
                          fontSize: '0.85em',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(67, 233, 123, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(67, 233, 123, 0.15)';
                        }}
                      >
                        Применить
                      </button>
                      <button
                        onClick={() => handleDeletePreset(preset.id, preset.name)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(231, 76, 60, 0.3)',
                          background: 'rgba(231, 76, 60, 0.15)',
                          color: '#e74c3c',
                          cursor: 'pointer',
                          fontWeight: 500,
                          fontSize: '0.85em',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(231, 76, 60, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(231, 76, 60, 0.15)';
                        }}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '0.9em' }}>
                Нет сохраненных пресетов. Сохраните текущие настройки как пресет, чтобы быстро применять их в будущем.
              </div>
            )}
          </div>
          </div>

          {/* Настройки сайдбара */}
          <div style={{ 
            padding: '16px', 
            background: 'rgba(109, 213, 237, 0.08)', 
            borderRadius: '12px', 
            border: '1px solid rgba(109, 213, 237, 0.2)',
            marginBottom: '8px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '16px',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#6dd5ed'
            }}>
              <span>📋</span>
              <span>Настройки сайдбара</span>
              <span style={{ 
                fontSize: '0.75em', 
                fontWeight: 500, 
                color: '#9ca3af',
                marginLeft: 'auto',
                padding: '4px 8px',
                background: 'rgba(109, 213, 237, 0.15)',
                borderRadius: '6px'
              }}>
                Только сайдбар
              </span>
          </div>

          {/* Управление снегом */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Управление снегом
            </label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => handleChange('snowEnabled', true)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${settings.snowEnabled === true ? '#43e97b' : '#2f3440'}`,
                  background: settings.snowEnabled === true ? 'rgba(67,233,123,0.15)' : '#0e1420',
                  color: settings.snowEnabled === true ? '#43e97b' : '#9ca3af',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95em',
                  transition: 'all 0.2s ease'
                }}
              >
                ❄ Включить снег
              </button>
              <button
                onClick={() => handleChange('snowEnabled', false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${settings.snowEnabled === false ? '#e74c3c' : '#2f3440'}`,
                  background: settings.snowEnabled === false ? 'rgba(231,76,60,0.15)' : '#0e1420',
                  color: settings.snowEnabled === false ? '#e74c3c' : '#9ca3af',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95em',
                  transition: 'all 0.2s ease'
                }}
              >
                🚫 Выключить снег
              </button>
              <button
                onClick={() => handleChange('snowEnabled', null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${settings.snowEnabled === null ? '#6dd5ed' : '#2f3440'}`,
                  background: settings.snowEnabled === null ? 'rgba(109,213,237,0.15)' : '#0e1420',
                  color: settings.snowEnabled === null ? '#6dd5ed' : '#9ca3af',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95em',
                  transition: 'all 0.2s ease'
                }}
              >
                🔄 Автоматически
              </button>
            </div>
            <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '8px' }}>
              {settings.snowEnabled === true && 'Снег всегда включен'}
              {settings.snowEnabled === false && 'Снег всегда выключен'}
              {settings.snowEnabled === null && 'Снег включается автоматически в зимний сезон (декабрь-февраль)'}
            </div>
          </div>

          {/* Управление аватаркой */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Настройка аватарки
            </label>
            <div style={{ fontSize: '0.85em', color: '#9ca3af', marginBottom: '12px' }}>
              Управление позицией и размером аватарки пользователя. Загрузка аватарки доступна в сайдбаре.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по горизонтали: {settings.avatarPositionX !== undefined ? settings.avatarPositionX : 0}px
                </label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="5"
                  value={settings.avatarPositionX !== undefined ? settings.avatarPositionX : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('avatarPositionX', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-100px (влево)</span>
                  <span>0px (центр)</span>
                  <span>100px (вправо)</span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по вертикали: {settings.avatarPositionY !== undefined ? settings.avatarPositionY : 0}px
                </label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="5"
                  value={settings.avatarPositionY !== undefined ? settings.avatarPositionY : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('avatarPositionY', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-100px (вверх)</span>
                  <span>0px (центр)</span>
                  <span>100px (вниз)</span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Ширина: {settings.avatarWidth !== undefined ? settings.avatarWidth : 88}px
                </label>
                <input
                  type="range"
                  min="40"
                  max="200"
                  step="5"
                  value={settings.avatarWidth !== undefined ? settings.avatarWidth : 88}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('avatarWidth', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>40px</span>
                  <span>88px (по умолчанию)</span>
                  <span>200px</span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Высота: {settings.avatarHeight !== undefined ? settings.avatarHeight : 88}px
                </label>
                <input
                  type="range"
                  min="40"
                  max="200"
                  step="5"
                  value={settings.avatarHeight !== undefined ? settings.avatarHeight : 88}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('avatarHeight', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>40px</span>
                  <span>88px (по умолчанию)</span>
                  <span>200px</span>
                </div>
              </div>
              
              {/* Кнопки быстрого управления аватаркой */}
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(67, 233, 123, 0.05)', borderRadius: '8px', border: '1px solid rgba(67, 233, 123, 0.2)' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 600, color: '#43e97b', fontSize: '0.9em' }}>
                  Быстрое управление
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <button
                    onClick={() => handleChange('avatarPositionX', (settings.avatarPositionX || 0) - 10)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Сдвинуть влево на 10px"
                  >
                    ← Влево
                  </button>
                  <button
                    onClick={() => handleChange('avatarPositionX', (settings.avatarPositionX || 0) + 10)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Сдвинуть вправо на 10px"
                  >
                    Вправо →
                  </button>
                  <button
                    onClick={() => handleChange('avatarPositionY', (settings.avatarPositionY || 0) - 10)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Сдвинуть вверх на 10px"
                  >
                    ↑ Вверх
                  </button>
                  <button
                    onClick={() => handleChange('avatarPositionY', (settings.avatarPositionY || 0) + 10)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Сдвинуть вниз на 10px"
                  >
                    Вниз ↓
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    onClick={() => {
                      const newSize = Math.max(40, (settings.avatarWidth || 88) - 10);
                      handleChange('avatarWidth', newSize);
                      handleChange('avatarHeight', newSize);
                    }}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Уменьшить на 10px"
                  >
                    ➖ Уменьшить
                  </button>
                  <button
                    onClick={() => {
                      const newSize = Math.min(200, (settings.avatarWidth || 88) + 10);
                      handleChange('avatarWidth', newSize);
                      handleChange('avatarHeight', newSize);
                    }}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                    title="Увеличить на 10px"
                  >
                    ➕ Увеличить
                  </button>
                </div>
                <button
                  onClick={() => {
                    handleChange('avatarPositionX', 0);
                    handleChange('avatarPositionY', 0);
                    handleChange('avatarWidth', 88);
                    handleChange('avatarHeight', 88);
                  }}
                  style={{
                    marginTop: '8px',
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    background: 'rgba(67, 233, 123, 0.1)',
                    color: '#43e97b',
                    cursor: 'pointer',
                    fontSize: '0.85em',
                    fontWeight: 500
                  }}
                  title="Сбросить к значениям по умолчанию"
                >
                  🔄 Сбросить позицию и размер
                </button>
              </div>
            </div>
          </div>

          {/* Настройки позиции текста "Issa Plus" */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Позиция текста "Issa Plus"
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по горизонтали: {settings.textPositionX !== undefined ? settings.textPositionX : 0}px
                </label>
                <input
                  type="range"
                  min="-200"
                  max="200"
                  step="5"
                  value={settings.textPositionX !== undefined ? settings.textPositionX : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('textPositionX', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-200px (влево)</span>
                  <span>0px (центр)</span>
                  <span>200px (вправо)</span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по вертикали: {settings.textPositionY !== undefined ? settings.textPositionY : 0}px
                </label>
                <input
                  type="range"
                  min="-200"
                  max="200"
                  step="5"
                  value={settings.textPositionY !== undefined ? settings.textPositionY : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('textPositionY', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-200px (вверх)</span>
                  <span>0px (центр)</span>
                  <span>200px (вниз)</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={() => handleChange('textPositionX', (settings.textPositionX || 0) - 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть влево на 10px"
                >
                  ← Влево
                </button>
                <button
                  onClick={() => handleChange('textPositionX', (settings.textPositionX || 0) + 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вправо на 10px"
                >
                  Вправо →
                </button>
                <button
                  onClick={() => handleChange('textPositionY', (settings.textPositionY || 0) - 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вверх на 10px"
                >
                  ↑ Вверх
                </button>
                <button
                  onClick={() => handleChange('textPositionY', (settings.textPositionY || 0) + 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вниз на 10px"
                >
                  Вниз ↓
                </button>
              </div>
              <button
                onClick={() => {
                  handleChange('textPositionX', 0);
                  handleChange('textPositionY', 0);
                }}
                style={{
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid rgba(67, 233, 123, 0.3)',
                  background: 'rgba(67, 233, 123, 0.1)',
                  color: '#43e97b',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: 500
                }}
                title="Сбросить позицию"
              >
                🔄 Сбросить позицию
              </button>
            </div>
          </div>

          {/* Настройки текста "Мульти - мессенджер" */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
              Настройки текста "Мульти - мессенджер"
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Текст подзаголовка
                </label>
                <input
                  type="text"
                  value={settings.subtitleText || 'Мульти - мессенджер'}
                  onChange={(e) => handleChange('subtitleText', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="Мульти - мессенджер"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Размер шрифта: {settings.subtitleFontSize || '1.1em'}
                </label>
                <input
                  type="text"
                  value={settings.subtitleFontSize || '1.1em'}
                  onChange={(e) => handleChange('subtitleFontSize', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="1.1em, 16px, 1rem"
                />
                <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                  Примеры: 1.1em, 16px, 1rem
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Цвет текста
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={settings.subtitleColor || '#fff'}
                    onChange={(e) => handleChange('subtitleColor', e.target.value)}
                    style={{
                      width: '60px',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={settings.subtitleColor || '#fff'}
                    onChange={(e) => handleChange('subtitleColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em'
                    }}
                    placeholder="#fff"
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по горизонтали: {settings.subtitlePositionX !== undefined ? settings.subtitlePositionX : 0}px
                </label>
                <input
                  type="range"
                  min="-200"
                  max="200"
                  step="5"
                  value={settings.subtitlePositionX !== undefined ? settings.subtitlePositionX : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('subtitlePositionX', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-200px (влево)</span>
                  <span>0px (центр)</span>
                  <span>200px (вправо)</span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                  Позиция по вертикали: {settings.subtitlePositionY !== undefined ? settings.subtitlePositionY : 0}px
                </label>
                <input
                  type="range"
                  min="-200"
                  max="200"
                  step="5"
                  value={settings.subtitlePositionY !== undefined ? settings.subtitlePositionY : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('subtitlePositionY', value);
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                  <span>-200px (вверх)</span>
                  <span>0px (центр)</span>
                  <span>200px (вниз)</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={() => handleChange('subtitlePositionX', (settings.subtitlePositionX || 0) - 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть влево на 10px"
                >
                  ← Влево
                </button>
                <button
                  onClick={() => handleChange('subtitlePositionX', (settings.subtitlePositionX || 0) + 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вправо на 10px"
                >
                  Вправо →
                </button>
                <button
                  onClick={() => handleChange('subtitlePositionY', (settings.subtitlePositionY || 0) - 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вверх на 10px"
                >
                  ↑ Вверх
                </button>
                <button
                  onClick={() => handleChange('subtitlePositionY', (settings.subtitlePositionY || 0) + 10)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Сдвинуть вниз на 10px"
                >
                  Вниз ↓
                </button>
              </div>
              <button
                onClick={() => {
                  handleChange('subtitleText', 'Мульти - мессенджер');
                  handleChange('subtitleFontSize', '1.1em');
                  handleChange('subtitleColor', '#fff');
                  handleChange('subtitlePositionX', 0);
                  handleChange('subtitlePositionY', 0);
                }}
                style={{
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid rgba(67, 233, 123, 0.3)',
                  background: 'rgba(67, 233, 123, 0.1)',
                  color: '#43e97b',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: 500
                }}
                title="Сбросить к значениям по умолчанию"
              >
                🔄 Сбросить настройки
              </button>
            </div>
          </div>

          {/* Управление снеговиком */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={settings.snowmanEnabled || false}
                onChange={(e) => handleChange('snowmanEnabled', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Показать снеговика
            </label>
            {settings.snowmanEnabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginLeft: '26px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Загрузить изображение снеговика
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSnowmanImageUpload}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '0.9em',
                      cursor: 'pointer'
                    }}
                  />
                  {settings.snowmanImage && (
                    <button
                      onClick={() => handleChange('snowmanImage', null)}
                      style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e74c3c',
                        background: 'rgba(231,76,60,0.15)',
                        color: '#e74c3c',
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: '0.85em'
                      }}
                    >
                      Удалить изображение
                    </button>
                  )}
                  {!settings.snowmanImage && (
                    <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                      Если не загружено, будет использовано изображение по умолчанию
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Тип позиционирования
                  </label>
                  <select
                    value={settings.snowmanPositionType || 'relative'}
                    onChange={(e) => handleChange('snowmanPositionType', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em'
                    }}
                  >
                    <option value="relative">Относительно текста</option>
                    <option value="absolute">Абсолютно по блоку</option>
                  </select>
                  <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                    {settings.snowmanPositionType === 'relative' ? 'Позиция относительно текста названия' : 'Позиция относительно всего блока сайдбара'}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Позиция по горизонтали (влево-вправо): {settings.snowmanPositionX !== undefined ? settings.snowmanPositionX : 0}px
                  </label>
                  <input
                    type="range"
                    min="-300"
                    max="300"
                    step="5"
                    value={settings.snowmanPositionX !== undefined ? settings.snowmanPositionX : 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleChange('snowmanPositionX', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-300px (влево)</span>
                    <span>0px (центр)</span>
                    <span>300px (вправо)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Позиция по вертикали (вверх-вниз): {settings.snowmanPositionY !== undefined ? settings.snowmanPositionY : 0}px
                  </label>
                  <input
                    type="range"
                    min="-300"
                    max="300"
                    step="5"
                    value={settings.snowmanPositionY !== undefined ? settings.snowmanPositionY : 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleChange('snowmanPositionY', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-300px (вверх)</span>
                    <span>0px (центр)</span>
                    <span>300px (вниз)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Размер: {settings.snowmanScale !== undefined ? settings.snowmanScale : 100}%
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="200"
                    step="5"
                    value={settings.snowmanScale !== undefined ? settings.snowmanScale : 100}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleChange('snowmanScale', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>50% (меньше)</span>
                    <span>100% (норма)</span>
                    <span>200% (больше)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
            </>
          )}

          {activeTab === 'welcome' && (
            <>
          <div style={{
            marginBottom: 16,
            padding: 14,
            background: 'rgba(15,23,42,0.65)',
            borderRadius: 12,
            border: '1px solid rgba(163,230,53,0.28)',
          }}>
            <div style={{ fontWeight: 700, color: '#a3e635', marginBottom: 10, fontSize: '1.05em' }}>
              Редактор приветственного экрана
            </div>
            <div style={{
              display: 'flex',
              flexDirection: compactModalLayout ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 10,
              alignItems: compactModalLayout ? 'stretch' : 'center',
              marginBottom: 8,
            }}>
              <button
                type="button"
                onClick={() => setWelcomeScreenEditorVariant('desktop')}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: welcomeScreenEditorVariant === 'desktop' ? '2px solid #a3e635' : '1px solid #374151',
                  background: welcomeScreenEditorVariant === 'desktop' ? 'rgba(163,230,53,0.15)' : '#111827',
                  color: welcomeScreenEditorVariant === 'desktop' ? '#e2e8f0' : '#9ca3af',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.92em',
                  width: compactModalLayout ? '100%' : 'auto',
                }}
              >
                Десктоп (центральная область)
              </button>
              <button
                type="button"
                onClick={() => setWelcomeScreenEditorVariant('mobile')}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: welcomeScreenEditorVariant === 'mobile' ? '2px solid #a3e635' : '1px solid #374151',
                  background: welcomeScreenEditorVariant === 'mobile' ? 'rgba(163,230,53,0.15)' : '#111827',
                  color: welcomeScreenEditorVariant === 'mobile' ? '#e2e8f0' : '#9ca3af',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.92em',
                  width: compactModalLayout ? '100%' : 'auto',
                }}
              >
                Мобильная модалка
              </button>
              <button
                type="button"
                onClick={copyWelcomeDesktopToMobile}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid rgba(96,165,250,0.45)',
                  background: 'rgba(59,130,246,0.12)',
                  color: '#93c5fd',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.92em',
                  width: compactModalLayout ? '100%' : 'auto',
                }}
                title="Скопировать все текущие десктопные настройки приветствия в мобильный слой"
              >
                Скопировать с десктопа → мобильная
              </button>
            </div>
            <div style={{ fontSize: '0.82em', color: '#94a3b8', lineHeight: 1.45 }}>
              На мобильном варианте превью показывает рамку телефона (~390×780 px). Тексты, цвета и фон без мобильных правок берутся с десктопа;{' '}
              <strong style={{ color: '#cbd5e1' }}>название и подзаголовок по умолчанию стоят по центру экрана</strong> (подзаголовок чуть выше, название чуть ниже), а не со смещением десктопа — сдвиги задаются отдельно в этом режиме или после «Скопировать с десктопа».
              {welcomeScreenEditorVariant === 'mobile' && (
                <span>
                  {' '}
                  В этом режиме <strong style={{ color: '#cbd5e1' }}>текст названия и подзаголовка</strong> — отдельные поля: можно задать другие формулировки, цвет, размер шрифта, позицию и масштаб; десктоп при этом не меняется, пока вы не редактируете режим «Десктоп».
                </span>
              )}
            </div>
          </div>
          {/* Настройки текста и снега для приветственного экрана */}
          <div style={{
            padding: '16px',
            background: 'rgba(163, 230, 53, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(163, 230, 53, 0.2)',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#a3e635'
            }}>
              <span>📝</span>
              <span>Настройки текста и снега</span>
            </div>
            
            {/* Настройка снега */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={previewWelcomeSettings.welcomeScreenSnowEnabled || false}
                  onChange={(e) => patchWelcomeKey('welcomeScreenSnowEnabled', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Показать снег на приветственном экране
              </label>
            </div>

            {/* Текст названия */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Текст названия{welcomeScreenEditorVariant === 'mobile' ? ' — только мобильная модалка' : ''}
              </label>
              <input
                type="text"
                value={previewWelcomeSettings.welcomeScreenText || 'Issa Plus'}
                onChange={(e) => patchWelcomeKey('welcomeScreenText', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                placeholder="Введите название приложения"
              />
            </div>

            {/* Текст подзаголовка */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Текст подзаголовка{welcomeScreenEditorVariant === 'mobile' ? ' — только мобильная модалка' : ''}
              </label>
              <input
                type="text"
                value={previewWelcomeSettings.welcomeScreenSubtitleText || 'Мульти - мессенджер'}
                onChange={(e) => patchWelcomeKey('welcomeScreenSubtitleText', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                placeholder="Мульти - мессенджер"
              />
            </div>

            {/* Шрифт названия */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Шрифт названия
              </label>
              {(() => {
                const customOptionValue = previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl
                  ? `'${previewWelcomeSettings.welcomeScreenCustomFontName}', Arial, sans-serif`
                  : '';
                
                const selectValue = previewWelcomeSettings.welcomeScreenFontFamily || 'Arial, sans-serif';
                
                return (
                  <select
                    value={selectValue}
                    onChange={(e) => patchWelcomeKey('welcomeScreenFontFamily', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em',
                      marginBottom: '12px'
                    }}
                  >
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Impact, sans-serif">Impact</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    {(Array.isArray(previewWelcomeSettings.welcomeScreenGoogleFontFamilies)
                      ? previewWelcomeSettings.welcomeScreenGoogleFontFamilies
                      : []
                    )
                      .filter(Boolean)
                      .map((name) => (
                        <option key={`wgf-title-${name}`} value={googleFontFamilyCssValue(name)}>
                          {name} (Google Fonts)
                        </option>
                      ))}
                    {previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl && (
                      <option value={customOptionValue}>{previewWelcomeSettings.welcomeScreenCustomFontName} (локальный)</option>
                    )}
                  </select>
                );
              })()}
            </div>

            {welcomeScreenEditorVariant === 'mobile' && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={() => {
                    googleFontsPickerApplyToRef.current = 'title';
                    setGoogleFontsPickerOpen(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(163, 230, 53, 0.45)',
                    background: 'rgba(163, 230, 53, 0.12)',
                    color: '#d9f99d',
                    fontSize: '0.95em',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <FiExternalLink size={18} />
                  Google Fonts — добавить в список
                </button>
                <div style={{ fontSize: '0.8em', color: '#9ca3af', marginTop: '8px', lineHeight: 1.45 }}>
                  Откроется каталог и сайт fonts.google.com. Любой шрифт с сайта можно вписать вручную — имя должно совпадать с названием семейства на Google Fonts.
                </div>
                {Array.isArray(previewWelcomeSettings.welcomeScreenGoogleFontFamilies) &&
                  previewWelcomeSettings.welcomeScreenGoogleFontFamilies.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                      {previewWelcomeSettings.welcomeScreenGoogleFontFamilies.map((name) => (
                        <span
                          key={name}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: '999px',
                            background: 'rgba(55, 65, 81, 0.9)',
                            color: '#e5e7eb',
                            fontSize: '0.82em',
                          }}
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeWelcomeGoogleFont(name)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#f87171',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: '1.1em',
                              lineHeight: 1,
                            }}
                            aria-label={`Убрать ${name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            )}

            {/* Размер шрифта названия */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Размер шрифта названия
              </label>
              <input
                type="text"
                value={previewWelcomeSettings.welcomeScreenFontSize || '2em'}
                onChange={(e) => patchWelcomeKey('welcomeScreenFontSize', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                placeholder="Например: 2em, 32px, 2rem"
              />
            </div>

            {/* Размер шрифта подзаголовка */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Размер шрифта подзаголовка
              </label>
              <input
                type="text"
                value={previewWelcomeSettings.welcomeScreenSubtitleFontSize || '1.1em'}
                onChange={(e) => patchWelcomeKey('welcomeScreenSubtitleFontSize', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                placeholder="1.1em, 16px, 1rem"
              />
            </div>

            {/* Шрифт */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Шрифт
              </label>
              {(() => {
                const customOptionValue = previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl
                  ? `'${previewWelcomeSettings.welcomeScreenCustomFontName}', Arial, sans-serif`
                  : '';
                
                const selectValue = previewWelcomeSettings.welcomeScreenFontFamily || 'Arial, sans-serif';
                
                return (
                  <select
                    value={selectValue}
                    onChange={(e) => patchWelcomeKey('welcomeScreenFontFamily', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em',
                      marginBottom: '12px'
                    }}
                  >
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Impact, sans-serif">Impact</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    {(Array.isArray(previewWelcomeSettings.welcomeScreenGoogleFontFamilies)
                      ? previewWelcomeSettings.welcomeScreenGoogleFontFamilies
                      : []
                    )
                      .filter(Boolean)
                      .map((name) => (
                        <option key={`wgf-body-${name}`} value={googleFontFamilyCssValue(name)}>
                          {name} (Google Fonts)
                        </option>
                      ))}
                    {previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl && (
                      <option value={customOptionValue}>{previewWelcomeSettings.welcomeScreenCustomFontName} (локальный)</option>
                    )}
                  </select>
                );
              })()}
              
              {/* Загрузка кастомного шрифта (URL или файл) */}
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0', fontSize: '0.95em' }}>
                  Загрузить шрифт из интернета или файла (опционально)
                </label>
                <div style={{ fontSize: '0.85em', color: '#9ca3af', marginBottom: '8px' }}>
                  Для Google Fonts: https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap
                </div>
                <input
                  type="text"
                  value={previewWelcomeSettings.welcomeScreenCustomFontUrl || ''}
                  onChange={(e) => patchWelcomeKey('welcomeScreenCustomFontUrl', e.target.value)}
                  placeholder="URL шрифта (CSS файл)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em',
                    marginBottom: '8px'
                  }}
                />
                <div style={{ fontSize: '0.85em', color: '#9ca3af', margin: '8px 0 4px 0' }}>или выберите локальный файл шрифта (ttf, otf, woff, woff2):</div>
                <input
                  type="file"
                  accept=".ttf,.otf,.woff,.woff2"
                  onChange={handleWelcomeScreenFontFileUpload}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '0.95em',
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                />
                {/* Если шрифт уже применён — показать информацию и кнопку сбросить */}
                {previewWelcomeSettings.welcomeScreenCustomFontDataUrl && previewWelcomeSettings.welcomeScreenCustomFontName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 8 }}>
                    <span style={{ color: '#a3e635', fontSize: '0.95em' }}>✓ Шрифт применён: {previewWelcomeSettings.welcomeScreenCustomFontName}</span>
                    <button
                      onClick={() => { 
                        patchWelcomeKey('welcomeScreenCustomFontDataUrl', ''); 
                        patchWelcomeKey('welcomeScreenCustomFontName', ''); 
                        patchWelcomeKey('welcomeScreenFontFamily', 'Arial, sans-serif');
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e74c3c',
                        background: 'rgba(231,76,60,0.15)',
                        color: '#e74c3c',
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      Сбросить
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Цвет названия */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Цвет названия
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={previewWelcomeSettings.welcomeScreenColor || '#43e97b'}
                  onChange={(e) => patchWelcomeKey('welcomeScreenColor', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={previewWelcomeSettings.welcomeScreenColor || '#43e97b'}
                  onChange={(e) => patchWelcomeKey('welcomeScreenColor', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="#43e97b"
                />
              </div>
            </div>

            {/* Цвет подзаголовка */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Цвет подзаголовка
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={previewWelcomeSettings.welcomeScreenSubtitleColor || '#fff'}
                  onChange={(e) => patchWelcomeKey('welcomeScreenSubtitleColor', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={previewWelcomeSettings.welcomeScreenSubtitleColor || '#fff'}
                  onChange={(e) => patchWelcomeKey('welcomeScreenSubtitleColor', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="#fff"
                />
              </div>
            </div>

            {/* Использовать градиент */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={previewWelcomeSettings.welcomeScreenUseGradient || false}
                  onChange={(e) => patchWelcomeKey('welcomeScreenUseGradient', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Использовать градиент для названия
              </label>
              {previewWelcomeSettings.welcomeScreenUseGradient && (
                <div style={{ marginTop: '12px', marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ width: '100px', color: '#cbd5e1', fontSize: '0.9em' }}>Начало:</label>
                    <input
                      type="color"
                      value={previewWelcomeSettings.welcomeScreenGradientStart || '#43e97b'}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGradientStart', e.target.value)}
                      style={{
                        width: '60px',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={previewWelcomeSettings.welcomeScreenGradientStart || '#43e97b'}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGradientStart', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '1em'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ width: '100px', color: '#cbd5e1', fontSize: '0.9em' }}>Конец:</label>
                    <input
                      type="color"
                      value={previewWelcomeSettings.welcomeScreenGradientEnd || '#2193b0'}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGradientEnd', e.target.value)}
                      style={{
                        width: '60px',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={previewWelcomeSettings.welcomeScreenGradientEnd || '#2193b0'}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGradientEnd', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '1em'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Эффекты свечения */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#e2e8f0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={previewWelcomeSettings.welcomeScreenGlowEnabled !== false}
                  onChange={(e) => patchWelcomeKey('welcomeScreenGlowEnabled', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Включить свечение
              </label>
              {previewWelcomeSettings.welcomeScreenGlowEnabled && (
                <div style={{ marginTop: '12px', marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                      Цвет свечения
                    </label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={previewWelcomeSettings.welcomeScreenGlowColor || '#43e97b'}
                        onChange={(e) => patchWelcomeKey('welcomeScreenGlowColor', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          borderRadius: '8px',
                          border: '1px solid #2f3440',
                          cursor: 'pointer'
                        }}
                      />
                      <input
                        type="text"
                        value={previewWelcomeSettings.welcomeScreenGlowColor || '#43e97b'}
                        onChange={(e) => patchWelcomeKey('welcomeScreenGlowColor', e.target.value)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #2f3440',
                          background: '#0e1420',
                          color: '#fff',
                          fontSize: '1em'
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                      Интенсивность: {previewWelcomeSettings.welcomeScreenGlowIntensity || 12}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      step="1"
                      value={previewWelcomeSettings.welcomeScreenGlowIntensity || 12}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGlowIntensity', parseInt(e.target.value, 10))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                      Разброс: {previewWelcomeSettings.welcomeScreenGlowSpread || 32}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={previewWelcomeSettings.welcomeScreenGlowSpread || 32}
                      onChange={(e) => patchWelcomeKey('welcomeScreenGlowSpread', parseInt(e.target.value, 10))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                      Тип эффекта
                    </label>
                    <select
                      value={previewWelcomeSettings.welcomeScreenEffectType || 'neon'}
                      onChange={(e) => patchWelcomeKey('welcomeScreenEffectType', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '1em'
                      }}
                    >
                      <option value="neon">Неоновое свечение</option>
                      <option value="shadow">Тень</option>
                      <option value="outline">Обводка</option>
                      <option value="sparkle">Искры</option>
                      <option value="gradient-animation">Градиентная анимация</option>
                      <option value="new-year">Новогоднее</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Позиция названия */}
            <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(67, 233, 123, 0.08)', borderRadius: '12px', border: '1px solid rgba(67, 233, 123, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontWeight: 700, fontSize: '1.1em', color: '#43e97b' }}>
                <span>📍</span>
                <span>Позиция названия{welcomeScreenEditorVariant === 'mobile' ? ' (мобильная модалка)' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Горизонтальная позиция (X): {previewWelcomeSettings.welcomeScreenTextPositionX || 0}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenTextPositionX || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenTextPositionX', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (влево)</span>
                    <span>0px (центр)</span>
                    <span>500px (вправо)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Вертикальная позиция (Y): {previewWelcomeSettings.welcomeScreenTextPositionY || 0}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenTextPositionY || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenTextPositionY', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (вверх)</span>
                    <span>0px (центр)</span>
                    <span>500px (вниз)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Масштаб названия: {previewWelcomeSettings.welcomeScreenTextScale || 100}% (ползунок или колёсико на превью)
                  </label>
                  <input
                    type="range"
                    min={50}
                    max={200}
                    step={5}
                    value={previewWelcomeSettings.welcomeScreenTextScale || 100}
                    onChange={(e) => patchWelcomeKey('welcomeScreenTextScale', parseInt(e.target.value, 10))}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>50%</span>
                    <span>100%</span>
                    <span>200%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Позиция подзаголовка */}
            <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(67, 233, 123, 0.08)', borderRadius: '12px', border: '1px solid rgba(67, 233, 123, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontWeight: 700, fontSize: '1.1em', color: '#43e97b' }}>
                <span>📍</span>
                <span>Позиция подзаголовка{welcomeScreenEditorVariant === 'mobile' ? ' (мобильная модалка)' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Горизонтальная позиция (X): {previewWelcomeSettings.welcomeScreenSubtitlePositionX || 0}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenSubtitlePositionX || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenSubtitlePositionX', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (влево)</span>
                    <span>0px (центр)</span>
                    <span>500px (вправо)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Вертикальная позиция (Y): {previewWelcomeSettings.welcomeScreenSubtitlePositionY || 0}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenSubtitlePositionY || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenSubtitlePositionY', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (вверх)</span>
                    <span>0px (центр)</span>
                    <span>500px (вниз)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Масштаб подзаголовка: {previewWelcomeSettings.welcomeScreenSubtitleScale || 100}% (ползунок или колёсико на превью)
                  </label>
                  <input
                    type="range"
                    min={50}
                    max={200}
                    step={5}
                    value={previewWelcomeSettings.welcomeScreenSubtitleScale || 100}
                    onChange={(e) => patchWelcomeKey('welcomeScreenSubtitleScale', parseInt(e.target.value, 10))}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>50%</span>
                    <span>100%</span>
                    <span>200%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Настройки текста описания/поздравления */}
          <div style={{
            padding: '16px',
            background: 'rgba(163, 230, 53, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(163, 230, 53, 0.2)',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#a3e635'
            }}>
              <span>📝</span>
              <span>Текст описания/поздравления</span>
            </div>

            {/* Шрифт описания — те же опции, что у названия */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Шрифт описания
              </label>
              {(() => {
                const customOptionValue = previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl
                  ? `'${previewWelcomeSettings.welcomeScreenCustomFontName}', Arial, sans-serif`
                  : '';
                const selectValue = previewWelcomeSettings.welcomeScreenDescriptionFontFamily || 'Arial, sans-serif';
                return (
                  <select
                    value={selectValue}
                    onChange={(e) => patchWelcomeKey('welcomeScreenDescriptionFontFamily', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em',
                      marginBottom: '12px',
                    }}
                  >
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Impact, sans-serif">Impact</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    {(Array.isArray(previewWelcomeSettings.welcomeScreenGoogleFontFamilies)
                      ? previewWelcomeSettings.welcomeScreenGoogleFontFamilies
                      : []
                    )
                      .filter(Boolean)
                      .map((name) => (
                        <option key={`wgf-desc-${name}`} value={googleFontFamilyCssValue(name)}>
                          {name} (Google Fonts)
                        </option>
                      ))}
                    {previewWelcomeSettings.welcomeScreenCustomFontName && previewWelcomeSettings.welcomeScreenCustomFontDataUrl && (
                      <option value={customOptionValue}>{previewWelcomeSettings.welcomeScreenCustomFontName} (локальный)</option>
                    )}
                  </select>
                );
              })()}
            </div>

            {welcomeScreenEditorVariant === 'mobile' && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={() => {
                    googleFontsPickerApplyToRef.current = 'description';
                    setGoogleFontsPickerOpen(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(163, 230, 53, 0.45)',
                    background: 'rgba(163, 230, 53, 0.12)',
                    color: '#d9f99d',
                    fontSize: '0.95em',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <FiExternalLink size={18} />
                  Google Fonts для описания — добавить и применить
                </button>
                <div style={{ fontSize: '0.8em', color: '#9ca3af', marginTop: '8px', lineHeight: 1.45 }}>
                  Общий список шрифтов с блоком названия. Выбранный шрифт сразу станет шрифтом поля описания.
                </div>
                {Array.isArray(previewWelcomeSettings.welcomeScreenGoogleFontFamilies) &&
                  previewWelcomeSettings.welcomeScreenGoogleFontFamilies.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                      {previewWelcomeSettings.welcomeScreenGoogleFontFamilies.map((name) => (
                        <span
                          key={`desc-chip-${name}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: '999px',
                            background: 'rgba(55, 65, 81, 0.9)',
                            color: '#e5e7eb',
                            fontSize: '0.82em',
                          }}
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeWelcomeGoogleFont(name)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#f87171',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: '1.1em',
                              lineHeight: 1,
                            }}
                            aria-label={`Убрать ${name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            )}
            
            {/* Rich text editor для текста описания */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Текст описания
              </label>
              <div style={{ position: 'relative' }}>
                {/* Toolbar */}
                <div
                  style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}
                  onPointerDownCapture={handleDescriptionToolbarPointerDownCapture}
                >
                  <button type="button" onClick={() => execDescriptionFormat('bold')} style={{ background: '#e0f7fa', border: '1px solid #b2ebf2', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800, color: '#232931' }}>B</button>
                  <button type="button" onClick={() => execDescriptionFormat('italic')} style={{ background: '#e8eaf6', border: '1px solid #c5cae9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontStyle: 'italic', color: '#232931' }}>I</button>
                  <button type="button" onClick={() => execDescriptionFormat('underline')} style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', textDecoration: 'underline', color: '#232931' }}>U</button>
                  <button type="button" onClick={() => execDescriptionFormat('insertUnorderedList')} style={{ background: '#f1f8e9', border: '1px solid #dcedc8', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>• Список</button>
                  <button type="button" onClick={() => execDescriptionFormat('insertOrderedList')} style={{ background: '#f1f8e9', border: '1px solid #dcedc8', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>1. Список</button>
                  <button type="button" onClick={() => execDescriptionFormat('justifyLeft')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>⟸</button>
                  <button type="button" onClick={() => execDescriptionFormat('justifyCenter')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>⟷</button>
                  <button type="button" onClick={() => execDescriptionFormat('justifyRight')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>⟹</button>
                  <button type="button" onClick={() => execDescriptionFormat('formatBlock', 'H1')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800, color: '#232931' }}>H1</button>
                  <button type="button" onClick={() => execDescriptionFormat('formatBlock', 'H2')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800, color: '#232931' }}>H2</button>
                  <button type="button" onClick={() => execDescriptionFormat('formatBlock', 'H3')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800, color: '#232931' }}>H3</button>
                  <button type="button" onClick={() => execDescriptionFormat('formatBlock', 'P')} style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 700, color: '#232931' }}>Обычный</button>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                    Цвет
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTextColor(v);
                        execDescriptionFormat('foreColor', v);
                      }}
                      onInput={(e) => {
                        const v = e.target.value;
                        setTextColor(v);
                        execDescriptionFormat('foreColor', v);
                      }}
                      style={{ width: 26, height: 26, border: 'none', background: 'transparent', padding: 0 }}
                    />
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                    Маркер
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBgColor(v);
                        execDescriptionFormat('hiliteColor', v);
                      }}
                      onInput={(e) => {
                        const v = e.target.value;
                        setBgColor(v);
                        execDescriptionFormat('hiliteColor', v);
                      }}
                      style={{ width: 26, height: 26, border: 'none', background: 'transparent', padding: 0 }}
                    />
                  </label>
                  <button type="button" onClick={() => execDescriptionFormat('removeFormat')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#232931' }}>Очистить</button>
                </div>
                {/* Rich editor */}
                <div
                  ref={descriptionEditorRef}
                  contentEditable
                  dir="ltr"
                  onInput={(e) => {
                    const html = e.currentTarget ? e.currentTarget.innerHTML : '';
                    patchWelcomeKey('welcomeScreenDescriptionText', html);
                  }}
                  suppressContentEditableWarning
                  style={{
                    width: '100%',
                    minHeight: 140,
                    padding: '14px 52px 14px 18px',
                    borderRadius: 12,
                    border: '1.5px solid #e74c3c',
                    background: '#fff',
                    fontSize: previewWelcomeSettings.welcomeScreenDescriptionFontSize || '1em',
                    fontFamily: previewWelcomeSettings.welcomeScreenDescriptionFontFamily || 'Arial, sans-serif',
                    outline: 'none',
                    overflowY: 'auto',
                    textAlign: 'left',
                    direction: 'ltr',
                    unicodeBidi: 'bidi-override',
                    color: previewWelcomeSettings.welcomeScreenDescriptionColor || '#232931',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowEmoji(v => !v)}
                  title="Эмодзи"
                  style={{ position: 'absolute', right: 8, top: 48, background: 'linear-gradient(135deg, #ffe082 0%, #fcb69f 100%)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', boxShadow: '0 1px 6px #e74c3c33', color: '#232931', fontWeight: 700 }}
                >
                  😊
                </button>
                {showEmoji && (
                  <div style={{ position: 'absolute', left: 0, top: '100%', marginTop: 100, zIndex: 1000, width: '1120px', maxWidth: 'calc(100% - 16px)' }} onClick={e => e.stopPropagation()}>
                    <CustomEmojiPicker
                      isOpen={showEmoji}
                      onClose={() => setShowEmoji(false)}
                      onEmojiSelect={(emoji) => {
                        const str = String(emoji || '');
                        if (str.startsWith('custom:')) insertCustomEmojiAtCursor(str);
                        else if (descriptionEditorRef.current) {
                          descriptionEditorRef.current.focus();
                          document.execCommand('insertText', false, str);
                          const currentHtml = descriptionEditorRef.current ? descriptionEditorRef.current.innerHTML : '';
                          patchWelcomeKey('welcomeScreenDescriptionText', currentHtml);
                        } else {
                          patchWelcomeKey('welcomeScreenDescriptionText', (previewWelcomeSettings.welcomeScreenDescriptionText || '') + str);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                Используйте панель инструментов для форматирования текста. Поддерживается многострочный текст.
              </div>
            </div>

            {/* Позиция описания */}
            <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(163, 230, 53, 0.08)', borderRadius: '12px', border: '1px solid rgba(163, 230, 53, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontWeight: 700, fontSize: '1.1em', color: '#a3e635' }}>
                <span>📍</span>
                <span>Позиция описания</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Горизонтальная позиция (X): {previewWelcomeSettings.welcomeScreenDescriptionPositionX || 0}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenDescriptionPositionX || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenDescriptionPositionX', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (влево)</span>
                    <span>0px (центр)</span>
                    <span>500px (вправо)</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                    Вертикальная позиция (Y): {previewWelcomeSettings.welcomeScreenDescriptionPositionY || 200}px
                  </label>
                  <input
                    type="range"
                    min="-500"
                    max="500"
                    step="5"
                    value={previewWelcomeSettings.welcomeScreenDescriptionPositionY || 200}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      patchWelcomeKey('welcomeScreenDescriptionPositionY', value);
                    }}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280', marginTop: '4px' }}>
                    <span>-500px (вверх)</span>
                    <span>200px (по умолчанию)</span>
                    <span>500px (вниз)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Размер шрифта описания */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Размер шрифта
              </label>
              <input
                type="text"
                value={previewWelcomeSettings.welcomeScreenDescriptionFontSize || '1em'}
                onChange={(e) => patchWelcomeKey('welcomeScreenDescriptionFontSize', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                placeholder="1em, 16px, 1rem"
              />
            </div>

            {/* Цвет текста описания */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Цвет текста
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={normalizeHexForColorInput(previewWelcomeSettings.welcomeScreenDescriptionColor)}
                  onChange={(e) => patchWelcomeKey('welcomeScreenDescriptionColor', e.target.value)}
                  onInput={(e) => patchWelcomeKey('welcomeScreenDescriptionColor', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={previewWelcomeSettings.welcomeScreenDescriptionColor || '#ffffff'}
                  onChange={(e) => patchWelcomeKey('welcomeScreenDescriptionColor', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '1em'
                  }}
                  placeholder="#ffffff"
                />
              </div>
            </div>

            {/* Максимальная ширина описания */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Максимальная ширина (px)
              </label>
              <input
                type="number"
                value={previewWelcomeSettings.welcomeScreenDescriptionMaxWidth || 600}
                onChange={(e) => patchWelcomeKey('welcomeScreenDescriptionMaxWidth', parseInt(e.target.value, 10) || 600)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em'
                }}
                min="200"
                max="1200"
              />
            </div>
          </div>

          {/* Настройки фона приветственного экрана */}
          <div style={{
            padding: '16px',
            background: 'rgba(163, 230, 53, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(163, 230, 53, 0.2)',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#a3e635'
            }}>
              <span>🎨</span>
              <span>Фон приветственного экрана</span>
            </div>
            
            {/* Тип фона */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                Тип фона
              </label>
              <select
                value={previewWelcomeSettings.welcomeScreenBackgroundType || 'gradient'}
                onChange={(e) => patchWelcomeKey('welcomeScreenBackgroundType', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2f3440',
                  background: '#0e1420',
                  color: '#fff',
                  fontSize: '1em',
                  marginBottom: '12px'
                }}
              >
                <option value="gradient">Градиент</option>
                <option value="solid">Однотонный цвет</option>
                <option value="image">Изображение</option>
              </select>
            </div>

            {/* Выбор градиента */}
            {previewWelcomeSettings.welcomeScreenBackgroundType === 'gradient' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                  Выберите градиент
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(5, 1fr)', 
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  {Object.keys(BACKGROUND_GRADIENTS).map((gradId, index) => (
                    <div
                      key={gradId}
                      onClick={() => patchWelcomeKey('welcomeScreenBackgroundGradient', gradId)}
                      style={{
                        height: '60px',
                        background: getBackgroundGradient(gradId),
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: previewWelcomeSettings.welcomeScreenBackgroundGradient === gradId ? '3px solid #a3e635' : '2px solid transparent',
                        transition: 'all 0.2s',
                        boxShadow: previewWelcomeSettings.welcomeScreenBackgroundGradient === gradId ? '0 0 12px rgba(163, 230, 53, 0.5)' : 'none'
                      }}
                      title={`Градиент ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Однотонный цвет */}
            {previewWelcomeSettings.welcomeScreenBackgroundType === 'solid' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                  Цвет фона
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={previewWelcomeSettings.welcomeScreenBackgroundColor || '#0f172a'}
                    onChange={(e) => patchWelcomeKey('welcomeScreenBackgroundColor', e.target.value)}
                    style={{
                      width: '60px',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={previewWelcomeSettings.welcomeScreenBackgroundColor || '#0f172a'}
                    onChange={(e) => patchWelcomeKey('welcomeScreenBackgroundColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '1em'
                    }}
                    placeholder="#0f172a"
                  />
                </div>
              </div>
            )}

            {/* Загрузка изображения фона */}
            {previewWelcomeSettings.welcomeScreenBackgroundType === 'image' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                  Изображение фона
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                      try {
                        const dataUrl = reader.result;
                        const probe = new Image();
                        await new Promise((resolve, reject) => {
                          probe.onload = resolve;
                          probe.onerror = () => reject(new Error('Не удалось прочитать изображение'));
                          probe.src = dataUrl;
                        });
                        const w = probe.naturalWidth;
                        const h = probe.naturalHeight;
                        const keepOriginal =
                          file.size <= WELCOME_BG_ORIGINAL_MAX_BYTES &&
                          w <= WELCOME_BG_ORIGINAL_MAX_EDGE &&
                          h <= WELCOME_BG_ORIGINAL_MAX_EDGE;
                        if (keepOriginal) {
                          patchWelcomeKey('welcomeScreenBackgroundImage', dataUrl);
                        } else {
                          const compressed = await compressImage(
                            dataUrl,
                            WELCOME_BG_FALLBACK_MAX_W,
                            WELCOME_BG_FALLBACK_MAX_H,
                            WELCOME_BG_ENCODE_QUALITY
                          );
                          patchWelcomeKey('welcomeScreenBackgroundImage', compressed);
                        }
                      } catch (error) {
                        console.error('Ошибка при обработке изображения фона:', error);
                        alert('Ошибка при обработке изображения. Попробуйте другое изображение.');
                      }
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #2f3440',
                    background: '#0e1420',
                    color: '#fff',
                    fontSize: '0.95em',
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                />
                {previewWelcomeSettings.welcomeScreenBackgroundImage && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.85em', color: '#9ca3af', marginBottom: '8px' }}>
                      Изображение загружено
                    </div>
                    <button
                      onClick={() => patchWelcomeKey('welcomeScreenBackgroundImage', null)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e74c3c',
                        background: 'rgba(231,76,60,0.15)',
                        color: '#e74c3c',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: 500
                      }}
                    >
                      Удалить изображение
                    </button>
                  </div>
                )}
                {!previewWelcomeSettings.welcomeScreenBackgroundImage && (
                  <div style={{ fontSize: '0.85em', color: '#9ca3af', marginTop: '4px' }}>
                    До 4 МБ и 4096x4096 px — сохраняется исходный файл без перекодирования. Крупнее — ужим до 4K, качество кодирования максимальное (WebP/JPEG).
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Превью приветственного экрана */}
          <div style={{
            padding: '16px',
            background: 'rgba(163, 230, 53, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(163, 230, 53, 0.2)',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 700,
                fontSize: '1.1em',
                color: '#a3e635'
              }}>
                <span>👁️</span>
                <span>Превью приветственного экрана</span>
              </div>
              <button
                onClick={() => setShowFullscreenPreview(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(163, 230, 53, 0.3)',
                  background: 'rgba(163, 230, 53, 0.1)',
                  color: '#a3e635',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9em',
                  transition: 'all 0.2s'
                }}
                title="Открыть превью на весь экран"
              >
                <FiMaximize2 size={16} />
                <span>На весь экран</span>
              </button>
            </div>

            {compactModalLayout && welcomeScreenEditorVariant === 'mobile' && (
              <div
                style={{
                  marginBottom: 14,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'linear-gradient(180deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.94) 100%)',
                  border: '1px solid rgba(71, 85, 105, 0.55)',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#64748b',
                    marginBottom: 8,
                    letterSpacing: 0.85,
                  }}
                >
                  Название — текущий вид
                </div>
                <WelcomeTitleCompactPreview ws={previewWelcomeSettings} />
              </div>
            )}
            
            {/* Превью контейнер (на мобильном варианте — рамка телефона ~390×780) */}
            <div
              style={{
                display: 'flex',
                justifyContent: welcomeScreenEditorVariant === 'mobile' ? 'center' : 'stretch',
                width: '100%',
              }}
            >
              <div
                style={
                  welcomeScreenEditorVariant === 'mobile'
                    ? {
                        border: '8px solid #1e293b',
                        borderRadius: 36,
                        overflow: 'hidden',
                        boxShadow: '0 20px 56px rgba(0,0,0,0.45)',
                        flexShrink: 0,
                        background: '#0f172a',
                      }
                    : { width: '100%' }
                }
              >
            <div
              data-preview-container
              onPointerMove={handlePreviewPointerMoveOnSurface}
              style={{
                position: 'relative',
                ...(welcomeScreenEditorVariant === 'mobile'
                  ? {
                      width: compactModalLayout ? 'min(390px, calc(100vw - 32px))' : 390,
                      maxWidth: '100%',
                      height: compactModalLayout ? 'min(780px, 72dvh)' : 780,
                      minHeight: compactModalLayout ? 'min(520px, 55dvh)' : 780,
                      maxHeight: compactModalLayout ? '72dvh' : 780,
                      margin: 0,
                      borderRadius: 0,
                      padding: compactModalLayout ? '28px 10px' : '36px 14px',
                      overflow: 'visible',
                      fontSize: '1em',
                      touchAction: 'none',
                    }
                  : {
                      width: '100%',
                      height: 'calc(100vh - 300px)',
                      minHeight: '600px',
                      maxHeight: 'calc(100vh - 200px)',
                      borderRadius: '22px',
                      margin: '24px 0',
                      padding: '40px',
                      overflow: 'auto',
                      fontSize: '1.2em',
                      touchAction: 'none',
                    }),
                ...welcomeScreenFillStyle(previewWelcomeSettings),
                color: '#fff',
                textAlign: 'center',
                cursor: (draggingImageIndex !== null || draggingTextElement) ? 'grabbing' : 'default',
                boxSizing: 'border-box',
              }}
            >
              <WelcomeScreenPhotoBackground
                imageUrl={
                  previewWelcomeSettings.welcomeScreenBackgroundType === 'image'
                    ? previewWelcomeSettings.welcomeScreenBackgroundImage
                    : null
                }
              />
              {(() => {
                const bgVideoIndex = (previewWelcomeSettings.welcomeScreenImages || []).findIndex(
                  (item) => item?.mediaType === 'video' && item?.isBackground && item?.image
                );
                const bgVideo = bgVideoIndex >= 0 ? previewWelcomeSettings.welcomeScreenImages[bgVideoIndex] : null;
                if (!bgVideo) return null;
                return (
                  <video
                    data-welcome-pinch-target={`image-${bgVideoIndex}`}
                    src={bgVideo.image}
                    muted={bgVideo.muted !== false}
                    loop={bgVideo.loop !== false}
                    autoPlay={bgVideo.autoplay !== false}
                    controls={!!bgVideo.controls}
                    playsInline
                    onPointerDown={(e) => handleImagePointerDown(e, bgVideoIndex)}
                    onWheel={(e) => handleImageWheel(e, bgVideoIndex)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: bgVideo.fitMode || 'cover',
                      transform: `scale(${(bgVideo.scaleX || 100) / 100}, ${(bgVideo.scaleY || 100) / 100})`,
                      transformOrigin: 'center',
                      zIndex: 5,
                      cursor: draggingImageIndex === bgVideoIndex ? 'grabbing' : 'grab',
                      outline: selectedImageIndex === bgVideoIndex ? '3px solid #a3e635' : 'none',
                      outlineOffset: '2px',
                      touchAction: 'none',
                    }}
                  />
                );
              })()}
              {/* Снег в превью */}
              {previewWelcomeSettings.welcomeScreenSnowEnabled && (
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12, overflow: 'visible' }}>
                  {Array.from({ length: 14 }).map((_, i) => (
                    <span
                      key={i}
                      style={{
                        position: 'absolute',
                        top: '-32px',
                        left: `${(i / 14) * 100}%`,
                        color: '#fff',
                        opacity: 0.85,
                        userSelect: 'none',
                        pointerEvents: 'none',
                        animation: `snow-fall-${i} ${4 + (i % 3)}s linear infinite`,
                        animationDelay: `${i * 0.45}s`,
                        fontSize: `${12 + (i % 4) * 2}px`,
                        textShadow: '0 0 8px #fff, 0 0 12px #6dd5ed',
                        ['--snow-x-offset']: `${-30 + (i % 7) * 12}px`
                      }}
                    >
                      ❄
                    </span>
                  ))}
                  <style>{`
                    ${Array.from({ length: 14 }).map((_, i) => `
                      @keyframes snow-fall-${i} {
                        0% { transform: translateY(0) translateX(0); opacity: 0.85; }
                        80% { opacity: 0.85; }
                        100% { transform: translateY(632px) translateX(${-30 + (i % 7) * 12}px); opacity: 0.2; }
                      }
                    `).join('')}
                  `}</style>
                </div>
              )}
              {/* Изображения в превью */}
              {(previewWelcomeSettings.welcomeScreenImages || []).map((img, index) => (
                img.image && !img.isBackground && (
                  img.mediaType === 'video' ? (
                    <video
                      key={index}
                      data-welcome-pinch-target={`image-${index}`}
                      src={img.image}
                      muted={img.muted !== false}
                      loop={img.loop !== false}
                      autoPlay={img.autoplay !== false}
                      controls={!!img.controls}
                      playsInline
                      onPointerDown={(e) => handleImagePointerDown(e, index)}
                      onWheel={(e) => handleImageWheel(e, index)}
                      style={{
                        position: 'absolute',
                        left: `calc(50% + ${img.positionX || 0}px)`,
                        top: `${img.positionY || 0}px`,
                        transform: 'translateX(-50%)',
                        width: `${img.width || 100}px`,
                        height: `${img.height || 100}px`,
                        objectFit: 'contain',
                        zIndex: 10,
                        cursor: draggingImageIndex === index ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        outline: selectedImageIndex === index ? '3px solid #a3e635' : 'none',
                        outlineOffset: '2px',
                        transition: draggingImageIndex === index ? 'none' : 'outline 0.2s',
                        touchAction: 'none',
                      }}
                    />
                  ) : (
                    <img
                      key={index}
                      data-welcome-pinch-target={`image-${index}`}
                      src={img.image}
                      alt={`Preview ${index + 1}`}
                      draggable={false}
                      onPointerDown={(e) => handleImagePointerDown(e, index)}
                      onWheel={(e) => handleImageWheel(e, index)}
                      style={{
                        position: 'absolute',
                        ...(img.fillWelcomeArea
                          ? {
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: img.fitMode || 'cover',
                              transform: `translate(${img.positionX || 0}px, ${img.positionY || 0}px) scale(${(img.imageDisplayScale ?? 100) / 100})`,
                              transformOrigin: 'center center',
                              zIndex: 8
                            }
                          : {
                              left: `calc(50% + ${img.positionX || 0}px)`,
                              top: `${img.positionY || 0}px`,
                              transform: `translateX(-50%) scale(${(img.imageDisplayScale ?? 100) / 100})`,
                              width: `${img.width || 100}px`,
                              height: `${img.height || 100}px`,
                              objectFit: 'contain',
                              zIndex: 10
                            }),
                        cursor: draggingImageIndex === index ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        outline: selectedImageIndex === index ? '3px solid #a3e635' : 'none',
                        outlineOffset: '2px',
                        transition: draggingImageIndex === index ? 'none' : 'outline 0.2s',
                        touchAction: 'none',
                      }}
                    />
                  )
                )
              ))}
              
              {/* Подзаголовок (перемещаемый и масштабируемый) */}
              <div
                key={previewWelcomeSettings.welcomeScreenSubtitleColor || 'subtitle'}
                data-welcome-pinch-target="subtitle"
                onPointerDown={(e) => handleTextPointerDown(e, 'subtitle')}
                onWheel={(e) => handleTextWheel(e, 'subtitle')}
                style={{
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 7,
                  fontSize: getPreviewWelcomeSubtitleFontSize(previewWelcomeSettings),
                  color: previewWelcomeSettings.welcomeScreenSubtitleColor || '#fff',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textShadow: '0 1px 8px #232931',
                  marginBottom: 2,
                  position: 'absolute',
                  top: `calc(50% + ${previewWelcomeSettings.welcomeScreenSubtitlePositionY || 0}px)`,
                  left: `calc(50% + ${previewWelcomeSettings.welcomeScreenSubtitlePositionX || 0}px)`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 60,
                  isolation: 'isolate',
                  cursor: draggingTextElement === 'subtitle' ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                  outline: draggingTextElement === 'subtitle' ? '2px dashed #a3e635' : 'none',
                  outlineOffset: '4px',
                  touchAction: 'none',
                }}
              >
                <span>💬</span>
                {previewWelcomeSettings.welcomeScreenSubtitleText || 'Мульти - мессенджер'}
              </div>
              
              {/* Блок с надписями (как в реальном приветственном экране) */}
              <div
                data-welcome-pinch-target="text"
                style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: 8, 
                marginTop: 2,
                position: 'absolute',
                top: `calc(50% + ${previewWelcomeSettings.welcomeScreenTextPositionY || 0}px)`,
                left: `calc(50% + ${previewWelcomeSettings.welcomeScreenTextPositionX || 0}px)`,
                transform: `translate(-50%, -50%) scale(${getPreviewWelcomeTextScale(previewWelcomeSettings)})`,
                zIndex: 60,
                isolation: 'isolate',
                pointerEvents: 'auto',
                cursor: draggingTextElement === 'text' ? 'grabbing' : 'grab',
                outline: draggingTextElement === 'text' ? '2px dashed #a3e635' : 'none',
                outlineOffset: '4px',
                userSelect: 'none',
                touchAction: 'none',
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                handleTextPointerDown(e, 'text');
              }}
              onWheel={(e) => {
                e.stopPropagation();
                handleTextWheel(e, 'text');
              }}
              >
                {(() => {
                  const wsSettings = previewWelcomeSettings;
                  const effectType = wsSettings.welcomeScreenEffectType || 'neon';
                  const style = {
                    fontFamily: wsSettings.welcomeScreenFontFamily || 'Arial, sans-serif',
                    fontSize: wsSettings.welcomeScreenFontSize || '2em',
                    fontWeight: 900,
                    marginBottom: 2,
                    lineHeight: 1.1,
                    letterSpacing: 1.2,
                    display: 'inline-block',
                    position: 'relative',
                    WebkitFontSmoothing: 'antialiased',
                  };
                  let className = '';
                  // Градиент
                  if (wsSettings.welcomeScreenUseGradient === true && effectType !== 'gradient-animation') {
                    style.background = `linear-gradient(135deg, ${wsSettings.welcomeScreenGradientStart || '#43e97b'} 0%, ${wsSettings.welcomeScreenGradientEnd || '#2193b0'} 100%)`;
                    style.WebkitBackgroundClip = 'text';
                    style.WebkitTextFillColor = 'transparent';
                    style.backgroundClip = 'text';
                    style.color = 'transparent';
                  } else if (effectType === 'gradient-animation' && wsSettings.welcomeScreenGlowEnabled) {
                    className = 'welcome-app-title-preview-grad';
                    style.color = 'transparent';
                  } else {
                    style.color = wsSettings.welcomeScreenColor || '#43e97b';
                    style.background = 'none';
                    style.WebkitBackgroundClip = 'unset';
                    style.WebkitTextFillColor = 'unset';
                    style.backgroundClip = 'unset';
                  }
                  // Эффекты свечения
                  const glowColor = wsSettings.welcomeScreenGlowColor ? String(wsSettings.welcomeScreenGlowColor).trim() : '#43e97b';
                  const intensity = Number(wsSettings.welcomeScreenGlowIntensity) || 12;
                  const spread = Number(wsSettings.welcomeScreenGlowSpread) || 32;
                  if (wsSettings.welcomeScreenGlowEnabled === true) {
                    switch (effectType) {
                      case 'shadow':
                        style.textShadow = `3px 3px 6px rgba(0,0,0,0.5), 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                        break;
                      case 'outline':
                        style.textShadow = `-1px -1px 0 ${glowColor}, 1px -1px 0 ${glowColor}, -1px 1px 0 ${glowColor}, 1px 1px 0 ${glowColor}, 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                        break;
                      case 'sparkle':
                        style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}, 0 0 ${spread * 1.5}px ${glowColor}, 2px 2px 4px rgba(0,0,0,0.3), -2px -2px 4px ${glowColor}88`;
                        break;
                      case 'gradient-animation':
                        break;
                      case 'new-year':
                        style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                        break;
                      case 'neon':
                      default:
                        className = 'welcome-app-title-preview-neon';
                        break;
                    }
                  } else {
                    style.textShadow = 'none';
                  }
                  const titlePaintKey = getWelcomePreviewTitlePaintKey(wsSettings);
                  return (
                    <React.Fragment key={titlePaintKey}>
                    <style>{`
                      @keyframes welcomeAppTitlePreviewNeonGlow {
                        0% { text-shadow: 0 0 ${intensity}px ${glowColor}, 0 0 ${spread * 0.5}px ${glowColor}, 0 0 2px #fff, 0 0 ${spread * 0.75}px ${glowColor}88; }
                        50% { text-shadow: 0 0 ${intensity * 2}px ${glowColor}, 0 0 ${spread}px ${glowColor}, 0 0 4px #fff, 0 0 ${spread}px ${glowColor}cc; }
                        100% { text-shadow: 0 0 ${intensity}px ${glowColor}, 0 0 ${spread * 0.5}px ${glowColor}, 0 0 2px #fff, 0 0 ${spread * 0.75}px ${glowColor}88; }
                      }
                      .welcome-app-title-preview-neon {
                        animation: welcomeAppTitlePreviewNeonGlow 1.6s infinite alternate;
                      }
                      .welcome-app-title-preview-grad {
                        background: linear-gradient(-45deg, ${glowColor}, ${wsSettings.welcomeScreenColor || '#43e97b'}, ${glowColor}, ${wsSettings.welcomeScreenColor || '#43e97b'}) !important;
                        background-size: 400% 400% !important;
                        animation: welcomeAppTitlePreviewGradientAnim 3s ease infinite !important;
                        -webkit-background-clip: text !important;
                        -webkit-text-fill-color: transparent !important;
                        background-clip: text !important;
                        color: transparent !important;
                      }
                      @keyframes welcomeAppTitlePreviewGradientAnim {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                      }
                    `}</style>
                    <span 
                      className={className}
                      style={{
                        ...style,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      {previewWelcomeSettings.welcomeScreenText || 'Issa Plus'}
                    </span>
                    </React.Fragment>
                  );
                })()}
              </div>
              
              {/* Текст описания в превью */}
              {previewWelcomeSettings.welcomeScreenDescriptionText && (
                <div
                  data-welcome-pinch-target="description"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    handleTextPointerDown(e, 'description');
                  }}
                  onWheel={(e) => {
                    e.stopPropagation();
                    handleTextWheel(e, 'description');
                  }}
                  style={{
                    position: 'absolute',
                    top: `calc(50% + ${previewWelcomeSettings.welcomeScreenDescriptionPositionY || 200}px)`,
                    left: `calc(50% + ${previewWelcomeSettings.welcomeScreenDescriptionPositionX || 0}px)`,
                    transform: `translate(-50%, -50%) scale(${(previewWelcomeSettings.welcomeScreenDescriptionScale || 100) / 100})`,
                    zIndex: 20,
                    cursor: draggingTextElement === 'description' ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    pointerEvents: 'auto',
                    outline: draggingTextElement === 'description' ? '2px dashed #a3e635' : 'none',
                    outlineOffset: '4px',
                    maxWidth: `${previewWelcomeSettings.welcomeScreenDescriptionMaxWidth || 600}px`,
                    fontFamily: previewWelcomeSettings.welcomeScreenDescriptionFontFamily || 'Arial, sans-serif',
                    fontSize: previewWelcomeSettings.welcomeScreenDescriptionFontSize || '1em',
                    color: previewWelcomeSettings.welcomeScreenDescriptionColor || '#fff',
                    textAlign: 'center',
                    lineHeight: 1.5,
                    wordWrap: 'break-word',
                    touchAction: 'none',
                  }}
                  dangerouslySetInnerHTML={{ __html: previewWelcomeSettings.welcomeScreenDescriptionText }}
                />
              )}
            </div>
              </div>
            </div>
            
            <div style={{ marginTop: '12px', fontSize: '0.85em', color: '#9ca3af', textAlign: 'center' }}>
              💡{' '}
              {compactModalLayout
                ? 'Перетаскивайте изображения и текст одним пальцем; масштаб — щипок двумя пальцами по нужному слою. До 5 слоёв — «+ Добавить медиа»; фото — ещё кнопки ± или ползунок.'
                : 'Перетаскивайте изображения, текст и описание мышкой, масштабируйте колесиком мыши'}
            </div>
          </div>

          {/* Выбор изображения для настройки */}
          {(previewWelcomeSettings.welcomeScreenImages || []).length > 0 && (
            <div style={{
              padding: '16px',
              background: 'rgba(163, 230, 53, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(163, 230, 53, 0.2)',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                fontWeight: 700,
                fontSize: '1.1em',
                color: '#a3e635'
              }}>
                <span>✅</span>
                <span>Выберите изображение для настройки</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(previewWelcomeSettings.welcomeScreenImages || []).map((img, index) => (
                  img.image && (
                    <label
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: selectedImageIndex === index ? 'rgba(163, 230, 53, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: selectedImageIndex === index ? '2px solid #a3e635' : '1px solid transparent',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedImageIndex === index}
                        onChange={() => setSelectedImageIndex(selectedImageIndex === index ? null : index)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      {img.mediaType === 'video' ? (
                        <video src={img.image} muted loop playsInline style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px' }} />
                      ) : (
                        <img src={img.image} alt={`Image ${index + 1}`} style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px' }} />
                      )}
                      <span style={{ fontWeight: 600, color: '#e2e8f0' }}>
                        {img.mediaType === 'video' ? `Видео ${index + 1}` : `Изображение ${index + 1}`}
                      </span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages.splice(index, 1);
                            patchWelcomeKey('welcomeScreenImages', newImages);
                            if (selectedImageIndex === index) setSelectedImageIndex(null);
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(231, 76, 60, 0.3)',
                            background: 'rgba(231, 76, 60, 0.15)',
                            color: '#e74c3c',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.85em'
                          }}
                        >
                          Удалить
                        </button>
                      </div>
                    </label>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Настройки выбранного изображения */}
          {selectedImageIndex !== null && previewWelcomeSettings.welcomeScreenImages?.[selectedImageIndex] && (
            <div style={{
              padding: '16px',
              background: 'rgba(163, 230, 53, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(163, 230, 53, 0.2)',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                fontWeight: 700,
                fontSize: '1.1em',
                color: '#a3e635'
              }}>
                <span>⚙️</span>
                <span>Настройки изображения {selectedImageIndex + 1}</span>
              </div>
              
              {(() => {
                const img = previewWelcomeSettings.welcomeScreenImages[selectedImageIndex];
                return (
                  <>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          const isVideo = file.type.startsWith('video/');
                          const maxSizeMB = isVideo ? 20 : 5;
                          if (file.size > maxSizeMB * 1024 * 1024) {
                            alert(`⚠️ Размер файла не должен превышать ${maxSizeMB} МБ`);
                            e.target.value = '';
                            return;
                          }
                          reader.onloadend = async () => {
                            try {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              if (isVideo) {
                                newImages[selectedImageIndex] = {
                                  ...newImages[selectedImageIndex],
                                  image: reader.result,
                                  mediaType: 'video',
                                  muted: newImages[selectedImageIndex]?.muted ?? true,
                                  loop: newImages[selectedImageIndex]?.loop ?? true,
                                  autoplay: newImages[selectedImageIndex]?.autoplay ?? true,
                                  controls: newImages[selectedImageIndex]?.controls ?? true,
                                  isBackground: newImages[selectedImageIndex]?.isBackground ?? false,
                                  fitMode: newImages[selectedImageIndex]?.fitMode ?? 'cover',
                                  scaleX: newImages[selectedImageIndex]?.scaleX ?? 100,
                                  scaleY: newImages[selectedImageIndex]?.scaleY ?? 100
                                };
                              } else {
                                const compressed = await compressImage(reader.result, 500, 500, 0.75);
                                newImages[selectedImageIndex] = {
                                  ...newImages[selectedImageIndex],
                                  image: compressed,
                                  mediaType: 'image',
                                  fillWelcomeArea: newImages[selectedImageIndex]?.fillWelcomeArea ?? false,
                                  imageDisplayScale: newImages[selectedImageIndex]?.imageDisplayScale ?? 100,
                                  fitMode: newImages[selectedImageIndex]?.fitMode ?? 'cover'
                                };
                              }
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            } catch (error) {
                              console.error('Ошибка при обработке файла:', error);
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '8px',
                        border: '1px solid #2f3440',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '0.9em',
                        marginBottom: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    {img.mediaType === 'video' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#cbd5e1', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={!!img.isBackground}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages.forEach((item, idx) => {
                                if (idx !== selectedImageIndex && item?.mediaType === 'video') {
                                  newImages[idx] = { ...item, isBackground: false };
                                }
                              });
                              newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], isBackground: e.target.checked };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                          />
                          Видео на весь экран
                        </label>
                        <label style={{ display: 'block', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Режим растяжения
                        </label>
                        <select
                          value={img.fitMode || 'cover'}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], fitMode: e.target.value };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: '1px solid #2f3440',
                            background: '#0e1420',
                            color: '#fff',
                            fontSize: '0.9em'
                          }}
                        >
                          <option value="cover">Заполнить (cover)</option>
                          <option value="contain">Вписать (contain)</option>
                          <option value="fill">Растянуть (fill)</option>
                        </select>
                        <label style={{ display: 'block', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Масштаб по ширине: {img.scaleX || 100}%
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="200"
                          step="5"
                          value={img.scaleX || 100}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], scaleX: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                        />
                        <label style={{ display: 'block', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Масштаб по высоте: {img.scaleY || 100}%
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="200"
                          step="5"
                          value={img.scaleY || 100}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], scaleY: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={img.muted !== false}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], muted: e.target.checked };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                          />
                          Без звука
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={img.loop !== false}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], loop: e.target.checked };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                          />
                          Зацикливать
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={img.autoplay !== false}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], autoplay: e.target.checked };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                          />
                          Автовоспроизведение
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={!!img.controls}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], controls: e.target.checked };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                          />
                          Показывать управление (пауза/воспроизведение)
                        </label>
                      </div>
                    )}
                    {img.mediaType !== 'video' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', border: '1px solid rgba(163,230,53,0.25)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, color: '#e2e8f0', fontSize: '0.95em', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!img.fillWelcomeArea}
                            onChange={(e) => {
                              const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                              newImages[selectedImageIndex] = {
                                ...newImages[selectedImageIndex],
                                fillWelcomeArea: e.target.checked,
                                fitMode: newImages[selectedImageIndex]?.fitMode || 'cover',
                                imageDisplayScale: newImages[selectedImageIndex]?.imageDisplayScale ?? 100
                              };
                              patchWelcomeKey('welcomeScreenImages', newImages);
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          Фото на всю область приветствия (как фон под текстом)
                        </label>
                        {img.fillWelcomeArea && (
                          <>
                            <label style={{ display: 'block', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                              Как вписать в область
                            </label>
                            <select
                              value={img.fitMode || 'cover'}
                              onChange={(e) => {
                                const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                                newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], fitMode: e.target.value };
                                patchWelcomeKey('welcomeScreenImages', newImages);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #2f3440',
                                background: '#0e1420',
                                color: '#fff',
                                fontSize: '0.9em'
                              }}
                            >
                              <option value="cover">Заполнить (cover)</option>
                              <option value="contain">Вписать (contain)</option>
                              <option value="fill">Растянуть (fill)</option>
                            </select>
                          </>
                        )}
                        <label style={{ display: 'block', fontWeight: 600, color: '#a3e635', fontSize: '0.9em' }}>
                          Масштаб: {img.imageDisplayScale ?? 100}% (ползунок или колёсико на превью)
                        </label>
                        <input
                          type="range"
                          min={50}
                          max={400}
                          step={5}
                          value={img.imageDisplayScale ?? 100}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], imageDisplayScale: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{ width: '100%' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#6b7280' }}>
                          <span>50%</span>
                          <span>100%</span>
                          <span>400%</span>
                        </div>
                        {compactModalLayout && (
                          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                            <button
                              type="button"
                              onClick={() => bumpWelcomeImageScale(selectedImageIndex, -10)}
                              style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: 10,
                                border: '1px solid rgba(163,230,53,0.35)',
                                background: 'rgba(163,230,53,0.12)',
                                color: '#a3e635',
                                fontWeight: 700,
                                fontSize: '1em',
                                cursor: 'pointer',
                              }}
                            >
                              − Масштаб
                            </button>
                            <button
                              type="button"
                              onClick={() => bumpWelcomeImageScale(selectedImageIndex, 10)}
                              style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: 10,
                                border: '1px solid rgba(163,230,53,0.35)',
                                background: 'rgba(163,230,53,0.12)',
                                color: '#a3e635',
                                fontWeight: 700,
                                fontSize: '1em',
                                cursor: 'pointer',
                              }}
                            >
                              + Масштаб
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: compactModalLayout ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Позиция X: {img.positionX || 0}px
                        </label>
                        <input
                          type="range"
                          min="-500"
                          max="500"
                          step="10"
                          value={img.positionX || 0}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], positionX: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Позиция Y: {img.positionY || 0}px
                        </label>
                        <input
                          type="range"
                          min="-500"
                          max="500"
                          step="10"
                          value={img.positionY || 0}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], positionY: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    {!(img.mediaType !== 'video' && img.fillWelcomeArea) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Ширина: {img.width || 100}px
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="500"
                          step="10"
                          value={img.width || 100}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], width: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#cbd5e1', fontSize: '0.9em' }}>
                          Высота: {img.height || 100}px
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="500"
                          step="10"
                          value={img.height || 100}
                          onChange={(e) => {
                            const newImages = [...(previewWelcomeSettings.welcomeScreenImages || [])];
                            newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], height: parseInt(e.target.value, 10) };
                            patchWelcomeKey('welcomeScreenImages', newImages);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Добавить новое изображение */}
          {(previewWelcomeSettings.welcomeScreenImages || []).length < 5 && (
            <div style={{
              padding: '16px',
              background: 'rgba(163, 230, 53, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(163, 230, 53, 0.2)',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => {
                  const newImages = [
                    ...(previewWelcomeSettings.welcomeScreenImages || []),
                    {
                      image: null,
                      positionX: 0,
                      positionY: 0,
                      width: 100,
                      height: 100,
                      mediaType: 'image',
                      muted: true,
                      loop: true,
                      autoplay: true,
                      controls: true,
                      isBackground: false,
                      fitMode: 'cover',
                      scaleX: 100,
                      scaleY: 100,
                      fillWelcomeArea: false,
                      imageDisplayScale: 100
                    }
                  ];
                  patchWelcomeKey('welcomeScreenImages', newImages);
                  setSelectedImageIndex((previewWelcomeSettings.welcomeScreenImages || []).length);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(163, 230, 53, 0.3)',
                  background: 'rgba(163, 230, 53, 0.15)',
                  color: '#a3e635',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95em'
                }}
              >
                + Добавить медиа
              </button>
            </div>
          )}
            </>
          )}

          {googleFontsPickerOpen &&
            ((activeTab === 'welcome' && welcomeScreenEditorVariant === 'mobile') ||
              (activeTab === 'sidebar' && compactModalLayout)) && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Google Fonts"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000200,
                background: 'rgba(0,0,0,0.72)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: compactModalLayout ? '0' : '16px',
              }}
              onClick={() => setGoogleFontsPickerOpen(false)}
            >
              <div
                style={{
                  maxHeight: compactModalLayout ? '88dvh' : '560px',
                  background: '#111827',
                  borderRadius: compactModalLayout ? '16px 16px 0 0' : '16px',
                  border: '1px solid #374151',
                  boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontWeight: 800, color: '#f9fafb', fontSize: '1.05em' }}>Google Fonts</span>
                  <button
                    type="button"
                    onClick={() => setGoogleFontsPickerOpen(false)}
                    style={{
                      border: 'none',
                      background: '#1f2937',
                      color: '#e5e7eb',
                      borderRadius: 8,
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Закрыть
                  </button>
                </div>
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => window.open('https://fonts.google.com', '_blank', 'noopener,noreferrer')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '12px',
                      borderRadius: 10,
                      border: '1px solid #4b5563',
                      background: '#1e3a5f',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.95em',
                    }}
                  >
                    <FiExternalLink size={18} />
                    Открыть fonts.google.com
                  </button>
                  <input
                    type="search"
                    value={googleFontsSearch}
                    onChange={(e) => setGoogleFontsSearch(e.target.value)}
                    placeholder="Поиск в каталоге…"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid #374151',
                      background: '#0e1420',
                      color: '#fff',
                      fontSize: '16px',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                    <input
                      type="text"
                      value={manualGoogleFontName}
                      onChange={(e) => setManualGoogleFontName(e.target.value)}
                      placeholder="Имя с сайта, например Caveat Brush"
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #374151',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '16px',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addWelcomeGoogleFont(manualGoogleFontName)}
                      style={{
                        padding: '0 16px',
                        borderRadius: 8,
                        border: 'none',
                        background: '#a3e635',
                        color: '#0f172a',
                        fontWeight: 800,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Добавить
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    overflowY: 'auto',
                    flex: 1,
                    padding: '4px 12px 16px',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {GOOGLE_FONTS_WELCOME_CATALOG.filter((f) =>
                    f.toLowerCase().includes(googleFontsSearch.trim().toLowerCase())
                  )
                    .slice(0, 120)
                    .map((family) => (
                      <button
                        key={family}
                        type="button"
                        onClick={() => addWelcomeGoogleFont(family)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 14px',
                          marginBottom: 4,
                          borderRadius: 8,
                          border: '1px solid #2f3440',
                          background: '#0e1420',
                          color: '#e5e7eb',
                          fontSize: '1em',
                          cursor: 'pointer',
                        }}
                      >
                        {family}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Кнопки */}
          <div style={{
            display: 'flex',
            flexDirection: compactModalLayout ? 'column-reverse' : 'row',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '24px',
          }}>
            <button
              onClick={handleReset}
              style={{
                padding: compactModalLayout ? '14px 20px' : '12px 24px',
                borderRadius: '10px',
                border: '1px solid #374151',
                background: '#111827',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: compactModalLayout ? '16px' : '1em',
                width: compactModalLayout ? '100%' : 'auto',
              }}
            >
              Сбросить
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              style={{
                padding: compactModalLayout ? '14px 20px' : '12px 24px',
                borderRadius: '10px',
                border: '1px solid rgba(67,233,123,0.35)',
                background: hasChanges ? 'rgba(67,233,123,0.15)' : 'rgba(67,233,123,0.05)',
                color: hasChanges ? '#b2ffb2' : '#6b7280',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                fontWeight: 700,
                fontSize: compactModalLayout ? '16px' : '1em',
                transition: 'all 0.2s ease',
                width: compactModalLayout ? '100%' : 'auto',
              }}
            >
              Подтвердить
            </button>
          </div>
          </div>

          {/* Превью - фиксированное справа (на телефоне — блок на всю ширину под формой) */}
          <div style={{
            position: compactModalLayout ? 'relative' : 'sticky',
            top: compactModalLayout ? 0 : '24px',
            backgroundColor: 'rgba(34, 40, 49, 0.8)',
            borderRadius: '12px',
            padding: '0',
            textAlign: 'center',
            border: '1px solid rgba(75, 85, 99, 0.3)',
            height: 'fit-content',
            maxHeight: compactModalLayout ? 'none' : 'calc(94vh - 120px)',
            overflow: 'hidden',
            width: compactModalLayout ? '100%' : '400px',
            maxWidth: '100%',
            justifySelf: 'stretch',
          }}>
            <div style={{ fontSize: '0.9em', color: '#9ca3af', marginBottom: '12px', fontWeight: 600, padding: '16px 16px 8px 16px' }}>Превью сайдбара:</div>
            {/* Имитация SidebarHeader */}
            <div style={{
              padding: '24px 32px 19px 32px',
              borderBottom: '1px solid #232931',
              background: 'linear-gradient(135deg, #232526 0%, #232931 100%)',
              boxShadow: '0 2px 12px rgba(44,62,80,0.10)',
              position: 'relative',
              overflow: 'visible',
              minHeight: '200px'
            }}>
              {/* Снеговик (абсолютное позиционирование по блоку) */}
              {settings.snowmanEnabled && settings.snowmanPositionType === 'absolute' && (
                <img
                  src={settings.snowmanImage || frostyImg}
                  alt="Снеговик"
                  style={{
                    position: 'absolute',
                    left: `calc(50% + ${settings.snowmanPositionX || 0}px)`,
                    top: `${settings.snowmanPositionY || 0}px`,
                    transform: 'translateX(-50%)',
                    width: `${(settings.snowmanScale || 100) * 0.6}px`,
                    height: 'auto',
                    zIndex: 10,
                    pointerEvents: 'none',
                    userSelect: 'none'
                  }}
                />
              )}
              {/* Снежинки (если включены) */}
              {settings.snowEnabled !== false && (
                <div style={{
                  position: 'absolute',
                  top: '46px',
                  left: 0,
                  right: 0,
                  height: '160px',
                  pointerEvents: 'none',
                  overflow: 'visible'
                }}>
                  {Array.from({ length: 5 }).map((_, index) => {
                    const offsets = [-30, -15, 0, 15, 30];
                    return (
                      <span
                        key={index}
                        style={{
                          position: 'absolute',
                          top: '-20px',
                          left: `${(index / 5) * 100}%`,
                          color: 'rgba(255, 255, 255, 0.92)',
                          fontSize: `${12 + (index % 4) * 2}px`,
                          animation: `snowFallPreview${index} ${4 + (index % 3)}s linear infinite`,
                          animationDelay: `${index * 0.45}s`,
                          filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.45))',
                          ['--snow-offset']: `${offsets[index]}px`
                        }}
                      >
                        ❄
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Контейнер с текстами */}
              <div style={{
                padding: '18px 0 8px 0',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '1.18em',
                letterSpacing: '0.01em',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative'
              }}>
                {/* Снеговик (относительно текста) */}
                {settings.snowmanEnabled && settings.snowmanPositionType === 'relative' && (
                  <img
                    src={settings.snowmanImage || frostyImg}
                    alt="Снеговик"
                    style={{
                      position: 'absolute',
                      left: `calc(50% + ${settings.snowmanPositionX || 0}px)`,
                      top: `${settings.snowmanPositionY || 0}px`,
                      transform: 'translateX(-50%)',
                      width: `${(settings.snowmanScale || 100) * 0.6}px`,
                      height: 'auto',
                      zIndex: 10,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />
                )}
                
                {/* Текст "Мессенджер" */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: -50 }}>
                  <span role="img" aria-label="chat">💬</span> Мессенджер
                </span>
              <style>{`
                @keyframes neonGlowPreview {
                  0% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${((Number(settings.glowSpread) || 32) * 0.5)}px ${settings.glowColor || '#43e97b'}, 
                                0 0 2px #fff, 
                                0 0 ${((Number(settings.glowSpread) || 32) * 0.75)}px ${settings.glowColor || '#43e97b'}88; 
                  }
                  50% { 
                    text-shadow: 0 0 ${((Number(settings.glowIntensity) || 12) * 2)}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}, 
                                0 0 8px #fff, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}cc; 
                  }
                  100% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${((Number(settings.glowSpread) || 32) * 0.5)}px ${settings.glowColor || '#43e97b'}, 
                                0 0 2px #fff, 
                                0 0 ${((Number(settings.glowSpread) || 32) * 0.75)}px ${settings.glowColor || '#43e97b'}88; 
                  }
                }
                @keyframes gradientAnimation {
                  0% { 
                    background-position: 0% 50%;
                  }
                  50% { 
                    background-position: 100% 50%;
                  }
                  100% { 
                    background-position: 0% 50%;
                  }
                }
                @keyframes sparkleAnimation {
                  0%, 100% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}, 
                                2px 2px 4px rgba(0,0,0,0.3),
                                -2px -2px 4px ${settings.glowColor || '#43e97b'}88;
                  }
                  25% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}, 
                                4px 4px 8px rgba(0,0,0,0.3),
                                -4px -4px 8px ${settings.glowColor || '#43e97b'}cc,
                                0 0 ${(Number(settings.glowSpread) || 32) * 1.5}px ${settings.glowColor || '#43e97b'};
                  }
                  50% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}, 
                                2px 2px 4px rgba(0,0,0,0.3),
                                -2px -2px 4px ${settings.glowColor || '#43e97b'}88;
                  }
                  75% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px ${settings.glowColor || '#43e97b'}, 
                                0 0 ${Number(settings.glowSpread) || 32}px ${settings.glowColor || '#43e97b'}, 
                                -4px -4px 8px rgba(0,0,0,0.3),
                                4px 4px 8px ${settings.glowColor || '#43e97b'}cc,
                                0 0 ${(Number(settings.glowSpread) || 32) * 1.5}px ${settings.glowColor || '#43e97b'};
                  }
                }
                .preview-neon-glow {
                  ${(settings.glowEnabled === true && settings.effectType === 'neon') ? 'animation: neonGlowPreview 1.6s infinite alternate;' : ''}
                }
                .preview-gradient-animation {
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? 'animation: gradientAnimation 3s ease infinite;' : ''}
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? `background: linear-gradient(-45deg, ${settings.glowColor || '#43e97b'}, ${settings.color || '#43e97b'}, ${settings.glowColor || '#43e97b'}, ${settings.color || '#43e97b'});` : ''}
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? 'background-size: 400% 400%;' : ''}
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? '-webkit-background-clip: text;' : ''}
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? '-webkit-text-fill-color: transparent;' : ''}
                  ${(settings.glowEnabled === true && settings.effectType === 'gradient-animation') ? 'background-clip: text;' : ''}
                }
                .preview-sparkle {
                  ${(settings.glowEnabled === true && settings.effectType === 'sparkle') ? 'animation: sparkleAnimation 2s ease-in-out infinite;' : ''}
                }
                @keyframes newYearAnimation {
                  0% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px #ff0000, 
                                0 0 ${Number(settings.glowSpread) || 32}px #ff0000, 
                                0 0 ${(Number(settings.glowSpread) || 32) * 0.5}px #ffd700,
                                2px 2px 4px rgba(255, 0, 0, 0.5);
                    filter: hue-rotate(0deg);
                  }
                  25% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px #00ff00, 
                                0 0 ${Number(settings.glowSpread) || 32}px #00ff00, 
                                0 0 ${(Number(settings.glowSpread) || 32) * 0.5}px #ffd700,
                                2px 2px 4px rgba(0, 255, 0, 0.5);
                    filter: hue-rotate(90deg);
                  }
                  50% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px #ffd700, 
                                0 0 ${Number(settings.glowSpread) || 32}px #ffd700, 
                                0 0 ${(Number(settings.glowSpread) || 32) * 0.5}px #ff0000,
                                2px 2px 4px rgba(255, 215, 0, 0.5);
                    filter: hue-rotate(180deg);
                  }
                  75% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px #ff1493, 
                                0 0 ${Number(settings.glowSpread) || 32}px #ff1493, 
                                0 0 ${(Number(settings.glowSpread) || 32) * 0.5}px #00ff00,
                                2px 2px 4px rgba(255, 20, 147, 0.5);
                    filter: hue-rotate(270deg);
                  }
                  100% { 
                    text-shadow: 0 0 ${Number(settings.glowIntensity) || 12}px #ff0000, 
                                0 0 ${Number(settings.glowSpread) || 32}px #ff0000, 
                                0 0 ${(Number(settings.glowSpread) || 32) * 0.5}px #ffd700,
                                2px 2px 4px rgba(255, 0, 0, 0.5);
                    filter: hue-rotate(360deg);
                  }
                }
                .preview-new-year {
                  ${(settings.glowEnabled === true && settings.effectType === 'new-year') ? 'animation: newYearAnimation 3s ease-in-out infinite;' : ''}
                }
                @keyframes snowFallPreview0 {
                  0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
                  10% { opacity: 0.9; }
                  100% { transform: translate3d(-30px, 160px, 0) rotate(360deg); opacity: 0; }
                }
                @keyframes snowFallPreview1 {
                  0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
                  10% { opacity: 0.9; }
                  100% { transform: translate3d(-15px, 160px, 0) rotate(360deg); opacity: 0; }
                }
                @keyframes snowFallPreview2 {
                  0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
                  10% { opacity: 0.9; }
                  100% { transform: translate3d(0px, 160px, 0) rotate(360deg); opacity: 0; }
                }
                @keyframes snowFallPreview3 {
                  0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
                  10% { opacity: 0.9; }
                  100% { transform: translate3d(15px, 160px, 0) rotate(360deg); opacity: 0; }
                }
                @keyframes snowFallPreview4 {
                  0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
                  10% { opacity: 0.9; }
                  100% { transform: translate3d(30px, 160px, 0) rotate(360deg); opacity: 0; }
                }
              `}</style>
              <span 
                className={(() => {
                  if (!settings.glowEnabled) return '';
                  const effectType = settings.effectType || 'neon';
                  if (effectType === 'neon') return 'preview-neon-glow';
                  if (effectType === 'gradient-animation') return 'preview-gradient-animation';
                  if (effectType === 'sparkle') return 'preview-sparkle';
                  if (effectType === 'new-year') return 'preview-new-year';
                  return '';
                })()}
                key={`preview-${settings.effectType}-${settings.glowColor}-${settings.glowIntensity}-${settings.glowSpread}-${settings.glowEnabled}-${settings.useGradient}-${settings.color}`}
                style={(() => {
                  const previewStyle = getPreviewStyle();
                  const finalStyle = {
                    ...previewStyle,
                    // Принудительное обновление стилей
                    display: 'block',
                    fontWeight: 700,
                  };
                  
                  // Для эффектов с анимацией не устанавливаем textShadow в inline стилях
                  const effectType = settings.effectType || 'neon';
                  if (!settings.glowEnabled) {
                    finalStyle.textShadow = 'none';
                  } else if (effectType === 'neon' || effectType === 'gradient-animation' || effectType === 'sparkle' || effectType === 'new-year') {
                    // Анимация будет управлять textShadow через CSS
                    // Не устанавливаем textShadow здесь
                  } else {
                    // Для статических эффектов (shadow, outline) устанавливаем textShadow
                    finalStyle.textShadow = previewStyle.textShadow || 'none';
                  }
                  
                  // Отладочная информация для проверки
                  console.log('Applying preview style:', { 
                    effectType: settings.effectType,
                    glowColor: settings.glowColor,
                    glowEnabled: settings.glowEnabled,
                    color: finalStyle.color, 
                    textShadow: finalStyle.textShadow, 
                    background: finalStyle.background
                  });
                  return finalStyle;
                })()}
              >
                {settings.text || 'Issa Plus'}
              </span>
              </div>
              
              {/* Имитация аватарки */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '16px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{
                  width: `${settings.avatarWidth || 88}px`,
                  height: `${settings.avatarHeight || 88}px`,
                  borderRadius: '12px',
                  background: settings.avatarImage ? 'transparent' : '#e3eaf2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  color: '#6b7280',
                  border: '2px solid rgba(109, 213, 237, 0.3)',
                  overflow: 'hidden',
                  position: 'relative',
                  left: `${settings.avatarPositionX || 0}px`,
                  top: `${settings.avatarPositionY || 0}px`
                }}>
                  {settings.avatarImage ? (
                    <img
                      src={settings.avatarImage}
                      alt="Аватарка"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                    />
                  ) : (
                    <span>👤</span>
                  )}
                </div>
                <div style={{
                  fontWeight: 700,
                  color: '#6dd5ed',
                  fontSize: '1.05rem'
                }}>
                  Пользователь
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Полноэкранный превью */}
      {showFullscreenPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 100000010,
            background: '#000',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Закрываем только при клике на фон (не на дочерние элементы)
            if (e.target === e.currentTarget) {
              setShowFullscreenPreview(false);
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
          onMouseMove={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Панель действий */}
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 10001,
              display: 'flex',
              gap: '12px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={!hasChanges}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '2px solid rgba(67,233,123,0.35)',
                background: hasChanges ? 'rgba(67,233,123,0.2)' : 'rgba(67,233,123,0.08)',
                color: hasChanges ? '#b2ffb2' : '#6b7280',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                fontWeight: 700,
                fontSize: '1em',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
              ✅ <span>Подтвердить</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFullscreenPreview(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '2px solid rgba(163, 230, 53, 0.5)',
                background: 'rgba(163, 230, 53, 0.2)',
                color: '#a3e635',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '1em',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(163, 230, 53, 0.3)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(163, 230, 53, 0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FiMinimize2 size={18} />
              <span>Закрыть (ESC)</span>
            </button>
          </div>

          {/* Полноэкранный контейнер превью */}
          <div
            style={{
              flex: 1,
              width: '100%',
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 12,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={
                welcomeScreenEditorVariant === 'mobile'
                  ? {
                      border: '8px solid #1e293b',
                      borderRadius: 36,
                      overflow: 'hidden',
                      boxShadow: '0 20px 56px rgba(0,0,0,0.45)',
                      flexShrink: 0,
                      background: '#0f172a',
                    }
                  : { width: '100%', height: '100%', minHeight: 0 }
              }
            >
          <div
            data-preview-container
            data-fullscreen-preview="true"
            onPointerMove={handlePreviewPointerMoveOnSurface}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'relative',
              ...(welcomeScreenEditorVariant === 'mobile'
                ? {
                    width: compactModalLayout ? 'min(390px, calc(100vw - 24px))' : 390,
                    maxWidth: '100%',
                    height: 'min(780px, 85vh)',
                    maxHeight: '85vh',
                    margin: 0,
                    padding: '32px 12px',
                    overflow: 'visible',
                    fontSize: '1em',
                    touchAction: 'none',
                  }
                : {
                    flex: 1,
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    margin: 0,
                    padding: '40px',
                    overflow: 'auto',
                    fontSize: '1.2em',
                    touchAction: 'none',
                  }),
              ...welcomeScreenFillStyle(previewWelcomeSettings),
              borderRadius: 0,
              color: '#fff',
              textAlign: 'center',
              cursor: (draggingImageIndex !== null || draggingTextElement) ? 'grabbing' : 'default',
              boxSizing: 'border-box',
              touchAction: 'none',
              userSelect: 'none'
            }}
          >
            <WelcomeScreenPhotoBackground
              imageUrl={
                previewWelcomeSettings.welcomeScreenBackgroundType === 'image'
                  ? previewWelcomeSettings.welcomeScreenBackgroundImage
                  : null
              }
            />
            {(() => {
              const bgVideoIndex = (previewWelcomeSettings.welcomeScreenImages || []).findIndex(
                (item) => item?.mediaType === 'video' && item?.isBackground && item?.image
              );
              const bgVideo = bgVideoIndex >= 0 ? previewWelcomeSettings.welcomeScreenImages[bgVideoIndex] : null;
              if (!bgVideo) return null;
              return (
                <video
                  data-welcome-pinch-target={`image-${bgVideoIndex}`}
                  src={bgVideo.image}
                  muted={bgVideo.muted !== false}
                  loop={bgVideo.loop !== false}
                  autoPlay={bgVideo.autoplay !== false}
                  controls={!!bgVideo.controls}
                  playsInline
                  onPointerDown={(e) => handleImagePointerDown(e, bgVideoIndex)}
                  onWheel={(e) => handleImageWheel(e, bgVideoIndex)}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: bgVideo.fitMode || 'cover',
                    transform: `translate(${bgVideo.positionX || 0}px, ${bgVideo.positionY || 0}px) scale(${(bgVideo.scaleX || 100) / 100}, ${(bgVideo.scaleY || 100) / 100})`,
                    transformOrigin: 'center',
                    zIndex: 5,
                    cursor: draggingImageIndex === bgVideoIndex ? 'grabbing' : 'grab',
                    outline: selectedImageIndex === bgVideoIndex ? '3px solid #a3e635' : 'none',
                    outlineOffset: '2px',
                    touchAction: 'none',
                  }}
                />
              );
            })()}
            {/* Снег в полноэкранном превью */}
            {previewWelcomeSettings.welcomeScreenSnowEnabled && (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12, overflow: 'visible' }}>
                {Array.from({ length: 14 }).map((_, i) => (
                  <span
                    key={i}
                    style={{
                      position: 'absolute',
                      top: '-32px',
                      left: `${(i / 14) * 100}%`,
                      color: '#fff',
                      opacity: 0.85,
                      userSelect: 'none',
                      pointerEvents: 'none',
                      animation: `snow-fall-fs-${i} ${4 + (i % 3)}s linear infinite`,
                      animationDelay: `${i * 0.45}s`,
                      fontSize: `${12 + (i % 4) * 2}px`,
                      textShadow: '0 0 8px #fff, 0 0 12px #6dd5ed',
                      ['--snow-x-offset']: `${-30 + (i % 7) * 12}px`
                    }}
                  >
                    ❄
                  </span>
                ))}
                <style>{`
                  ${Array.from({ length: 14 }).map((_, i) => `
                    @keyframes snow-fall-fs-${i} {
                      0% { transform: translateY(0) translateX(0); opacity: 0.85; }
                      80% { opacity: 0.85; }
                      100% { transform: translateY(100vh) translateX(${-30 + (i % 7) * 12}px); opacity: 0.2; }
                    }
                  `).join('')}
                `}</style>
              </div>
            )}

            {/* Изображения в полноэкранном превью */}
            {(previewWelcomeSettings.welcomeScreenImages || []).map((img, index) => (
              img.image && !img.isBackground && (
                img.mediaType === 'video' ? (
                  <video
                    key={index}
                    data-welcome-pinch-target={`image-${index}`}
                    src={img.image}
                    muted={img.muted !== false}
                    loop={img.loop !== false}
                    autoPlay={img.autoplay !== false}
                    controls={!!img.controls}
                    playsInline
                    onPointerDown={(e) => handleImagePointerDown(e, index)}
                    onWheel={(e) => handleImageWheel(e, index)}
                    style={{
                      position: 'absolute',
                      left: `calc(50% + ${img.positionX || 0}px)`,
                      top: `${img.positionY || 0}px`,
                      transform: 'translateX(-50%)',
                      width: `${img.width || 100}px`,
                      height: `${img.height || 100}px`,
                      objectFit: 'contain',
                      zIndex: 10,
                      cursor: draggingImageIndex === index ? 'grabbing' : 'grab',
                      userSelect: 'none',
                      outline: selectedImageIndex === index ? '3px solid #a3e635' : 'none',
                      outlineOffset: '2px',
                      transition: draggingImageIndex === index ? 'none' : 'outline 0.2s',
                      touchAction: 'none',
                    }}
                  />
                ) : (
                  <img
                    key={index}
                    data-welcome-pinch-target={`image-${index}`}
                    src={img.image}
                    alt={`Preview ${index + 1}`}
                    draggable={false}
                    onPointerDown={(e) => handleImagePointerDown(e, index)}
                    onWheel={(e) => handleImageWheel(e, index)}
                    style={{
                      position: 'absolute',
                      ...(img.fillWelcomeArea
                        ? {
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: img.fitMode || 'cover',
                            transform: `translate(${img.positionX || 0}px, ${img.positionY || 0}px) scale(${(img.imageDisplayScale ?? 100) / 100})`,
                            transformOrigin: 'center center',
                            zIndex: 8
                          }
                        : {
                            left: `calc(50% + ${img.positionX || 0}px)`,
                            top: `${img.positionY || 0}px`,
                            transform: `translateX(-50%) scale(${(img.imageDisplayScale ?? 100) / 100})`,
                            width: `${img.width || 100}px`,
                            height: `${img.height || 100}px`,
                            objectFit: 'contain',
                            zIndex: 10
                          }),
                      cursor: draggingImageIndex === index ? 'grabbing' : 'grab',
                      userSelect: 'none',
                      outline: selectedImageIndex === index ? '3px solid #a3e635' : 'none',
                      outlineOffset: '2px',
                      transition: draggingImageIndex === index ? 'none' : 'outline 0.2s',
                      touchAction: 'none',
                    }}
                  />
                )
              )
            ))}

            {/* Подзаголовок в полноэкранном превью */}
            <div
              key={`fs-sub-${previewWelcomeSettings.welcomeScreenSubtitleColor || 'subtitle'}`}
              data-welcome-pinch-target="subtitle"
              onPointerDown={(e) => handleTextPointerDown(e, 'subtitle')}
              onWheel={(e) => handleTextWheel(e, 'subtitle')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: getPreviewWelcomeSubtitleFontSize(previewWelcomeSettings),
                color: previewWelcomeSettings.welcomeScreenSubtitleColor || '#fff',
                fontWeight: 700,
                letterSpacing: 1.5,
                textShadow: '0 1px 8px #232931',
                marginBottom: 2,
                position: 'absolute',
                top: `calc(50% + ${previewWelcomeSettings.welcomeScreenSubtitlePositionY || 0}px)`,
                left: `calc(50% + ${previewWelcomeSettings.welcomeScreenSubtitlePositionX || 0}px)`,
                transform: 'translate(-50%, -50%)',
                zIndex: 60,
                isolation: 'isolate',
                cursor: draggingTextElement === 'subtitle' ? 'grabbing' : 'grab',
                userSelect: 'none',
                pointerEvents: 'auto',
                outline: draggingTextElement === 'subtitle' ? '2px dashed #a3e635' : 'none',
                outlineOffset: '4px',
                touchAction: 'none',
              }}
            >
              <span>💬</span>
              {previewWelcomeSettings.welcomeScreenSubtitleText || 'Мульти - мессенджер'}
            </div>

            {/* Название в полноэкранном превью */}
            <div
              data-welcome-pinch-target="text"
              style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 8,
              marginTop: 2,
              position: 'absolute',
              top: `calc(50% + ${previewWelcomeSettings.welcomeScreenTextPositionY || 0}px)`,
              left: `calc(50% + ${previewWelcomeSettings.welcomeScreenTextPositionX || 0}px)`,
              transform: `translate(-50%, -50%) scale(${getPreviewWelcomeTextScale(previewWelcomeSettings)})`,
              zIndex: 60,
              isolation: 'isolate',
              pointerEvents: 'auto',
              cursor: draggingTextElement === 'text' ? 'grabbing' : 'grab',
              outline: draggingTextElement === 'text' ? '2px dashed #a3e635' : 'none',
              outlineOffset: '4px',
              userSelect: 'none',
              touchAction: 'none',
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              handleTextPointerDown(e, 'text');
            }}
            onWheel={(e) => {
              e.stopPropagation();
              handleTextWheel(e, 'text');
            }}
            >
              {(() => {
                const wsSettings = previewWelcomeSettings;
                const effectType = wsSettings.welcomeScreenEffectType || 'neon';
                const style = {
                  fontFamily: wsSettings.welcomeScreenFontFamily || 'Arial, sans-serif',
                  fontSize: wsSettings.welcomeScreenFontSize || '2em',
                  fontWeight: 900,
                  marginBottom: 2,
                  lineHeight: 1.1,
                  letterSpacing: 1.2,
                  display: 'inline-block',
                  position: 'relative'
                };
                let className = '';
                const glowColor = wsSettings.welcomeScreenGlowColor ? String(wsSettings.welcomeScreenGlowColor).trim() : '#43e97b';
                const intensity = Number(wsSettings.welcomeScreenGlowIntensity) || 12;
                const spread = Number(wsSettings.welcomeScreenGlowSpread) || 32;
                if (wsSettings.welcomeScreenUseGradient === true && effectType !== 'gradient-animation') {
                  style.background = `linear-gradient(135deg, ${wsSettings.welcomeScreenGradientStart || '#43e97b'} 0%, ${wsSettings.welcomeScreenGradientEnd || '#2193b0'} 100%)`;
                  style.WebkitBackgroundClip = 'text';
                  style.WebkitTextFillColor = 'transparent';
                  style.backgroundClip = 'text';
                  style.color = 'transparent';
                } else if (effectType === 'gradient-animation' && wsSettings.welcomeScreenGlowEnabled) {
                  className = 'welcome-gradient-animation-fs';
                  style.color = 'transparent';
                } else {
                  style.color = wsSettings.welcomeScreenColor || '#43e97b';
                  style.background = 'none';
                  style.WebkitBackgroundClip = 'unset';
                  style.WebkitTextFillColor = 'unset';
                  style.backgroundClip = 'unset';
                }
                if (wsSettings.welcomeScreenGlowEnabled === true) {
                  switch (effectType) {
                    case 'shadow':
                      style.textShadow = `3px 3px 6px rgba(0,0,0,0.5), 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                      break;
                    case 'outline':
                      style.textShadow = `-1px -1px 0 ${glowColor}, 1px -1px 0 ${glowColor}, -1px 1px 0 ${glowColor}, 1px 1px 0 ${glowColor}, 0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                      break;
                    case 'sparkle':
                      style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}, 0 0 ${spread * 1.5}px ${glowColor}, 2px 2px 4px rgba(0,0,0,0.3), -2px -2px 4px ${glowColor}88`;
                      break;
                    case 'gradient-animation':
                      break;
                    case 'new-year':
                      style.textShadow = `0 0 ${intensity}px ${glowColor}, 0 0 ${spread}px ${glowColor}`;
                      break;
                    case 'neon':
                    default:
                      className = 'welcome-neon-glow-fs';
                      break;
                  }
                } else {
                  style.textShadow = 'none';
                }
                const titlePaintKeyFs = `fs-${getWelcomePreviewTitlePaintKey(wsSettings)}`;
                return (
                  <React.Fragment key={titlePaintKeyFs}>
                  <style>{`
                    @keyframes welcomeNeonGlowFs {
                      0% { text-shadow: 0 0 ${intensity}px ${glowColor}, 0 0 ${spread * 0.5}px ${glowColor}, 0 0 2px #fff, 0 0 ${spread * 0.75}px ${glowColor}88; }
                      50% { text-shadow: 0 0 ${intensity * 2}px ${glowColor}, 0 0 ${spread}px ${glowColor}, 0 0 4px #fff, 0 0 ${spread}px ${glowColor}cc; }
                      100% { text-shadow: 0 0 ${intensity}px ${glowColor}, 0 0 ${spread * 0.5}px ${glowColor}, 0 0 2px #fff, 0 0 ${spread * 0.75}px ${glowColor}88; }
                    }
                    .welcome-neon-glow-fs {
                      animation: welcomeNeonGlowFs 1.6s infinite alternate;
                    }
                    .welcome-gradient-animation-fs {
                      background: linear-gradient(-45deg, ${glowColor}, ${wsSettings.welcomeScreenColor || '#43e97b'}, ${glowColor}, ${wsSettings.welcomeScreenColor || '#43e97b'}) !important;
                      background-size: 400% 400% !important;
                      animation: welcomeGradientAnimFs 3s ease infinite !important;
                      -webkit-background-clip: text !important;
                      -webkit-text-fill-color: transparent !important;
                      background-clip: text !important;
                      color: transparent !important;
                    }
                    @keyframes welcomeGradientAnimFs {
                      0% { background-position: 0% 50%; }
                      50% { background-position: 100% 50%; }
                      100% { background-position: 0% 50%; }
                    }
                  `}</style>
                  <span
                    className={className}
                    style={{
                      ...style,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {previewWelcomeSettings.welcomeScreenText || 'Issa Plus'}
                  </span>
                  </React.Fragment>
                );
              })()}
            </div>

            {/* Описание в полноэкранном превью */}
            {previewWelcomeSettings.welcomeScreenDescriptionText && (
              <div
                data-welcome-pinch-target="description"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handleTextPointerDown(e, 'description');
                }}
                onWheel={(e) => {
                  e.stopPropagation();
                  handleTextWheel(e, 'description');
                }}
                style={{
                  position: 'absolute',
                  top: `calc(50% + ${previewWelcomeSettings.welcomeScreenDescriptionPositionY || 200}px)`,
                  left: `calc(50% + ${previewWelcomeSettings.welcomeScreenDescriptionPositionX || 0}px)`,
                  transform: `translate(-50%, -50%) scale(${(previewWelcomeSettings.welcomeScreenDescriptionScale || 100) / 100})`,
                  zIndex: 60,
                  cursor: draggingTextElement === 'description' ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                  outline: draggingTextElement === 'description' ? '2px dashed #a3e635' : 'none',
                  outlineOffset: '4px',
                  maxWidth: `${previewWelcomeSettings.welcomeScreenDescriptionMaxWidth || 600}px`,
                  fontFamily: previewWelcomeSettings.welcomeScreenDescriptionFontFamily || 'Arial, sans-serif',
                  fontSize: previewWelcomeSettings.welcomeScreenDescriptionFontSize || '1em',
                  color: previewWelcomeSettings.welcomeScreenDescriptionColor || '#fff',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  wordWrap: 'break-word',
                  touchAction: 'none',
                }}
                dangerouslySetInnerHTML={{ __html: previewWelcomeSettings.welcomeScreenDescriptionText }}
              />
            )}
          </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

